import { useState, useEffect, useRef, useMemo, useId } from 'react';
import { MapPin, AlertCircle } from 'lucide-react';
import { Storage } from '../../lib/storage';
import {
  fetchBookings,
  persistBookings,
  bookingsAreServerAuthoritative,
  createBookings,
  deleteBookingsByIds,
  deleteBookingsBySeries,
} from '../../lib/api/bookings';
import {
  validateBooking,
  gapFillableByDurations,
  createBooking,
  BOOKING_DEFAULT_BUFFER_MIN,
  serieEstSeasonEnd,
  localDateOf,
  serieGenerateSlots,
  serieBuildReservations,
  serieAssignMatchesToSlots,
  expandRecurrence,
  isHoliday,
  getHolidaySet,
  holidayName,
} from '../../engine';
import { useT } from '../../i18n';
import { OverrideRefusedError, sendsOverride } from '../../lib/api/bookingOverride';
import { useOverrideRight } from '../../state/overrideRight';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import {
  MONTH_LONG_KEYS,
  MONTH_SHORT_KEYS,
  WEEKDAY_LONG_KEYS,
  WEEKDAY_SHORT_KEYS,
} from './dateNames';

export interface SerieBookingPanelProps {
  /** The series/tournament whose bookings + matches are managed here. */
  series: any;
  /** Optional class id the panel was opened from (drives which time-room is expanded). */
  focusClassId?: any;
  /** The live booking draft (slotRooms, slotMode, recurrence, …). */
  draft: any;
  /** Updates the booking draft. */
  setDraft: any;
  /** Optional: persist placed matches (enables the "place matches" step). */
  onPlaceMatches?: any;
  /** Optional: notified when the calendar bookings change. */
  onBookingsChanged?: any;
  /** Whether this is a seriespill (vs a mini-tournament). */
  isSeriespill?: boolean;
  /** Optional: notified after new reservations are created. */
  onReserved?: any;
  /** Optional recurrence-slot node rendered under the time-rooms. */
  recurrenceSlot?: any;
}

// Banebooking panel for a series / mini-tournament — set up time-rooms, court
// availability, reserve in the Padelsenter calendar, place matches in slots,
// and review/clear bookings. Extracted verbatim from the original monolith
// (legacy/App.jsx); markup/logic unchanged. No `Padel24Engine` prop — it has no
// `Padel24Engine.` refs and renders no component children that were once in the monolith.
export function SerieBookingPanel({
  series,
  focusClassId = null,
  draft,
  setDraft,
  onPlaceMatches = null,
  onBookingsChanged = null,
  isSeriespill = false,
  onReserved = null,
  recurrenceSlot = null,
}: SerieBookingPanelProps) {
  const t = useT();
  // #949: the operator flag is asked for only by a session holding
  // `booking.override_constraints` (state/overrideRight.tsx).
  const overrideRight = useOverrideRight();
  // Titles of the panel's three dialogs (#900), from `useId` — the panel is
  // mounted by four different settings screens.
  const dialogUid = safeId('serie-booking', useId());
  const dialogIds = {
    confirmTitle: `${dialogUid}-confirm-title`,
    reserveTitle: `${dialogUid}-reserve-title`,
    reserveLead: `${dialogUid}-reserve-lead`,
    statusTitle: `${dialogUid}-status-title`,
  };
  const [expandedRoom, setExpandedRoom] = useState(() => {
    const rs = draft.slotRooms || [];
    // If we arrived here from a class: expand only the room that covers the class.
    // If there is no such room (e.g. Damer without its own time-room), keep everything collapsed.
    if (focusClassId) return rs.find((r) => (r.classIds || []).includes(focusClassId))?.id || null;
    if (rs.length === 1) return rs[0].id;
    return null;
  });
  const [reservePreview, setReservePreview] = useState(null); // { toCreate, conflicts, existing } | null
  const [reserveBusy, setReserveBusy] = useState(false);
  const [reserveResult, setReserveResult] = useState(null);
  const [bookingList, setBookingList] = useState(null); // null = not loaded yet
  const [openBkDates, setOpenBkDates] = useState({});
  // ── Geonorge address autocomplete (standalone «Gateadresse») ──
  // Free, official Kartverket API. Requires network (works on the published site).
  const [addrResults, setAddrResults] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const addrTimer = useRef(null);
  const addrSeq = useRef(0);
  const titleCasePoststed = (raw) =>
    (raw || '')
      .toLowerCase()
      .split(/(\s|-|\/)/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join('');
  const searchGeonorge = (q) => {
    if (addrTimer.current) clearTimeout(addrTimer.current);
    const query = (q || '').trim();
    if (query.length < 3) {
      setAddrResults([]);
      setAddrOpen(false);
      setAddrLoading(false);
      return;
    }
    setAddrLoading(true);
    const seq = ++addrSeq.current;
    addrTimer.current = setTimeout(async () => {
      try {
        const url = `https://ws.geonorge.no/adresser/v1/sok?sok=${encodeURIComponent(query)}*&treffPerSide=8&side=0`;
        const r = await fetch(url);
        const d = await r.json();
        if (seq !== addrSeq.current) return; // stale response – ignore
        const list = (d.adresser || [])
          .map((a) => ({
            tekst: a.adressetekst || [a.adressenavn, a.nummer, a.bokstav].filter(Boolean).join(' '),
            postnr: a.postnummer || '',
            poststed: titleCasePoststed(a.poststed || ''),
          }))
          .filter((x) => x.tekst);
        setAddrResults(list);
        setAddrOpen(list.length > 0);
      } catch {
        if (seq === addrSeq.current) {
          setAddrResults([]);
          setAddrOpen(false);
        }
      } finally {
        if (seq === addrSeq.current) setAddrLoading(false);
      }
    }, 250);
  };
  const [bookingCfg, setBookingCfg] = useState(null); // booking_config_v1 (holiday set)
  const [clubCourts, setClubCourts] = useState(0); // number of physical courts (club_info_v1)
  const [serieBookingCount, setSerieBookingCount] = useState(0);
  // BOOKING STATUS: the series' own bookings (for per-time-room counters) + status modal after reserve+place.
  const [serieBookings, setSerieBookings] = useState([]);
  const [confirmRemove, setConfirmRemove] = useState(null); // null | 'placement' | 'reservations' — confirmation popup
  // Number of placed (not-yet-played) matches in the series — controls whether «Nullstill plassering» is shown.
  const placedMatchCount = (series?.matches || []).filter((m) => !m.completed && m.date).length;
  const [statusReport, setStatusReport] = useState(null); // { reserved, conflicts, placed, unplaced, empty, emptyIds[] } | null
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cr = await Storage.get('booking_config_v1');
        if (alive && cr?.value) setBookingCfg(JSON.parse(cr.value));
      } catch {
        /* deviation: empty catch given a comment to satisfy no-empty; behaviour unchanged */
      }
      try {
        const ci = await Storage.get('club_info_v1');
        if (alive && ci?.value) {
          const c = JSON.parse(ci.value);
          if (c && c.courts) setClubCourts(c.courts);
        }
      } catch {
        /* deviation: empty catch given a comment to satisfy no-empty; behaviour unchanged */
      }
      try {
        const all = await fetchBookings();
        if (alive) {
          const mine = all.filter((b) => b.seriesId === series.id);
          setSerieBookings(mine);
          setSerieBookingCount(mine.length);
        }
      } catch {
        /* deviation: empty catch given a comment to satisfy no-empty; behaviour unchanged */
      }
    })();
    return () => {
      alive = false;
    };
    // deviation: dependency array preserved verbatim from the monolith; disable exhaustive-deps to keep behaviour identical
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The club's actual courts: courtCount (booking) overrides clubInfo.courts otherwise.
  const clubCourtList = (() => {
    const n = (bookingCfg && bookingCfg.courtCount) || clubCourts || 0;
    const meta = (bookingCfg && bookingCfg.courtMeta) || {};
    return Array.from({ length: n }, (_, i) => {
      const num = i + 1;
      return {
        num,
        name:
          (meta[num] && meta[num].nickname) || t('seriespill.booking.courtN', { num: String(num) }),
      };
    });
  })();

  const loadBookingList = async () => {
    try {
      setBookingList(await fetchBookings());
    } catch {
      setBookingList([]);
    }
  };

  // MINI-TOURNAMENTS (non-seriespill): standardize the time-room once when
  // the club's courts are loaded — all courts selected, today's date, 18:00 + 2 hours.
  const miniInitRef = useRef(false);
  useEffect(() => {
    if (isSeriespill || miniInitRef.current) return;
    if (!clubCourtList.length) return;
    miniInitRef.current = true;
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const addMin = (t, min) => {
      const [h, m] = (t || '18:00').split(':').map(Number);
      const tot = h * 60 + m + min;
      return `${String(Math.floor((tot % 1440) / 60)).padStart(2, '0')}:${String(tot % 60).padStart(2, '0')}`;
    };
    setDraft((s) => ({
      ...s,
      slotRooms: (s.slotRooms || []).map((r, i) =>
        i !== 0
          ? r
          : {
              ...r,
              recurring: false,
              singleDate: r.singleDate || today,
              startTime: r.startTime || '18:00',
              endTime: r.endTime || addMin(r.startTime || '18:00', 120),
              // Keep an existing court selection (e.g. from actual bookings via «Endre i
              // kalender»). Only a FRESH draft without courts gets «all courts selected».
              slotCount: r.courtList && r.courtList.length ? r.slotCount || r.courtList.length : 1,
              courtList:
                r.courtList && r.courtList.length
                  ? r.courtList
                  : clubCourtList.map((c) => ({ id: `crt_${c.num}`, num: c.num, name: c.name })),
            },
      ),
    }));
    // deviation: dependency array preserved verbatim from the monolith; disable exhaustive-deps to keep behaviour identical
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeriespill, clubCourtList]);

  // COURT AVAILABILITY (mini-tournaments): color each court by whether a NORMAL
  // user would be allowed to book it in the time-room (same smartbooking + config as
  // regular booking). green = free/allowed, red = hard-occupied (overlap), yellow = a
  // normal user would be rejected by a smart/config rule (admin can override).
  const [courtAvail, setCourtAvail] = useState({}); // { [num]: { status } }
  // deviation: lastTimeEdit/setLastTimeEdit -> _-prefixed (declared in the monolith but unused in this range) to satisfy no-unused-vars; behaviour unchanged
  const [_lastTimeEdit, _setLastTimeEdit] = useState(null); // 'start' | 'end' | null — for duration adjustment
  // Half-hour options for From/to (mini-tournaments) — guarantees XX:00 / XX:30.
  // deviation: timeOpts -> _timeOpts (declared in the monolith but unused in this range) to satisfy no-unused-vars; behaviour unchanged
  const _timeOpts = useMemo(() => {
    const a = [];
    for (let m = 0; m < 1440; m += 30)
      a.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
    return a;
  }, []);
  // Weekday name from an ISO date (local — not toISOString).
  const weekdayNo = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return '';
    return t(WEEKDAY_LONG_KEYS[new Date(y, m - 1, d).getDay()]);
  };
  const room0 = (draft.slotRooms || [])[0];
  // RESERVED STATE for the «Reserver baner» button (mini-tournaments): when the series
  // already has reserved courts, show that. If the time-room was moved afterwards,
  // offer «Oppdater reservasjon» (moves them) instead of looking unreserved.
  const serieReservedState = (() => {
    if (isSeriespill || !serieBookingCount) return 'none';
    if (draft.recurrence?.enabled) return 'reserved'; // recurrence: too complex to match every date — just show reserved
    const matches =
      room0 &&
      serieBookings.length > 0 &&
      serieBookings.every(
        (b) =>
          b.date === room0.singleDate &&
          b.startTime === room0.startTime &&
          b.endTime === room0.endTime,
      );
    return matches ? 'reserved' : 'mismatch';
  })();
  const courtNumsKey = clubCourtList.map((c) => c.num).join(',');
  const selCourtsKey = ((draft.slotRooms || [])[0]?.courtList || []).map((c) => c.num).join(',');
  const allowedDursCfg =
    bookingCfg?.allowedDurations && bookingCfg.allowedDurations.length
      ? bookingCfg.allowedDurations
      : [60, 90, 120];
  const allowedDursKey = allowedDursCfg.join(',');
  useEffect(() => {
    let alive = true;
    if (isSeriespill || !room0?.singleDate || !room0?.startTime || !room0?.endTime) {
      setCourtAvail({});
      return undefined;
    }
    const toMin = (t) => {
      const [h, m] = (t || '0:0').split(':').map(Number);
      return h * 60 + m;
    };
    if (toMin(room0.endTime) <= toMin(room0.startTime)) {
      setCourtAvail({});
      return undefined;
    }
    (async () => {
      try {
        const all = await fetchBookings();
        const date = room0.singleDate;
        // Exclude the series'/activity's own bookings (they should not count as occupied against themselves).
        const dayAll = all.filter((b) => b.date === date && b.seriesId !== series?.id);
        const minBM = Math.min(...allowedDursCfg);
        const smartOn = bookingCfg?.smartAllocation !== false;
        const bufferMin = bookingCfg?.bufferMin ?? BOOKING_DEFAULT_BUFFER_MIN;
        const enabled = clubCourtList.map((c) => c.num);
        // The courts you actually book in this block — they should NOT count as a «better
        // alternative» against each other (you take them all anyway). Only courts OUTSIDE the selection
        // can trigger the «another court is tighter» warning.
        const selectedNums = (room0.courtList || []).map((c) => c.num);
        const smartOpts = {
          bufferMin,
          minBookingMin: minBM,
          smartAllocation: true,
          totalCourts: enabled.length,
          enabledCourts: enabled,
          allowedDurations: allowedDursCfg,
          excludeCourts: selectedNums,
        };
        const physOpts = { bufferMin, minBookingMin: minBM, smartAllocation: false };
        const res = {};
        const fmtT = (mins) =>
          `${String(Math.floor((mins % 1440) / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
        const deadGapLimit = Math.max(bufferMin || 0, minBM || 0);
        const cs = toMin(room0.startTime),
          ce = toMin(room0.endTime);
        for (const c of clubCourtList) {
          const overlap = dayAll.some(
            (b) => b.court === c.num && toMin(b.startTime) < ce && toMin(b.endTime) > cs,
          );
          if (overlap) {
            res[c.num] = { status: 'red' };
            continue;
          }
          // Full smartbooking validation (same as a normal user encounters).
          const v = validateBooking(
            { court: c.num, date, startTime: room0.startTime, endTime: room0.endTime },
            dayAll,
            smartOn ? smartOpts : physOpts,
          );
          if (v.ok) {
            res[c.num] = { status: 'green' };
            continue;
          }
          // YELLOW: explain the RESULT of booking here — the unused time the 2h block
          // leaves up to the nearest previous/next booking on the court.
          const sameCourt = dayAll.filter((b) => b.court === c.num);
          let prevEnd = null,
            nextStart = null;
          for (const b of sameCourt) {
            const bs = toMin(b.startTime),
              be = toMin(b.endTime);
            if (be <= cs) prevEnd = prevEnd == null ? be : Math.max(prevEnd, be);
            if (bs >= ce) nextStart = nextStart == null ? bs : Math.min(nextStart, bs);
          }
          const gaps = [];
          if (prevEnd != null) {
            const g = cs - prevEnd;
            if (g > 0)
              gaps.push({
                mins: g,
                from: fmtT(prevEnd),
                to: fmtT(cs),
                dead: g < deadGapLimit || (smartOn && !gapFillableByDurations(g, allowedDursCfg)),
              });
          }
          if (nextStart != null) {
            const g = nextStart - ce;
            if (g > 0)
              gaps.push({
                mins: g,
                from: fmtT(ce),
                to: fmtT(nextStart),
                dead: g < deadGapLimit || (smartOn && !gapFillableByDurations(g, allowedDursCfg)),
              });
          }
          res[c.num] = { status: 'yellow', gaps, isolated: gaps.length === 0 };
        }
        // HOLISTIC: when you block-book more courts than there are «perfect»
        // (green) ones, the yellow ones are unavoidable. We APPROVE them (forced) so they can
        // be reserved, but keep the YELLOW color + warning — the admin should still see that
        // the smartbooking rules are broken. (Courts that were only yellow because another
        // BLOCK court was better are now already green via excludeCourts.)
        const needed = selectedNums.length;
        // deviation: type-only (monolith is @ts-nocheck) — Object.values infers `unknown`; cast at access.
        const greenCount = Object.values(res).filter((s) => (s as any).status === 'green').length;
        if (needed > greenCount) {
          for (const c of selectedNums) {
            if (res[c] && res[c].status === 'yellow') res[c] = { ...res[c], forced: true };
          }
        }
        if (alive) setCourtAvail(res);
      } catch {
        if (alive) setCourtAvail({});
      }
    })();
    return () => {
      alive = false;
    };
    // deviation: dependency array preserved verbatim from the monolith; disable exhaustive-deps to keep behaviour identical
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSeriespill,
    room0?.singleDate,
    room0?.startTime,
    room0?.endTime,
    courtNumsKey,
    selCourtsKey,
    bookingCfg?.bufferMin,
    bookingCfg?.smartAllocation,
    allowedDursKey,
    series?.id,
    serieBookingCount,
  ]);

  // Today's bookings (excluding the own series) for the placement timeline.
  const [dayBk, setDayBk] = useState([]);
  const [showAllCourts, setShowAllCourts] = useState(false); // timeline: show all courts, not only the selected ones
  const placeTrackRef = useRef(null); // timeline track (px width for drag→time)
  const placeDrag = useRef(null); // active drag state
  const [findMode, setFindMode] = useState('auto'); // next free day: auto(follow day)|any|weekday|weekend
  const [findMsg, setFindMsg] = useState('');
  useEffect(() => {
    let alive = true;
    if (isSeriespill || !room0?.singleDate) {
      setDayBk([]);
      return undefined;
    }
    (async () => {
      try {
        const all = await fetchBookings();
        const day = all.filter((b) => b.date === room0.singleDate && b.seriesId !== series?.id);
        if (alive) setDayBk(day);
      } catch {
        if (alive) setDayBk([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isSeriespill, room0?.singleDate, series?.id, serieBookingCount]);

  // Compute the estimated season end (same as buildPayload) — used as the deadline limit.
  const reserveEndISO = () => {
    if (series?.config?.deadline) return series.config.deadline;
    if (draft.dateMode === 'fixed' && draft.deadline) return draft.deadline;
    const allDivs = (series?.config?.classes || []).flatMap((c) => c.divisions || []);
    const est = serieEstSeasonEnd(
      draft.seasonStart,
      draft.roundIntervalValue,
      draft.roundIntervalUnit,
      allDivs,
      draft.doubleRound,
    );
    // LOCAL date (DOMAIN_RULES §4, #939); invalid → null, as the RangeError from
    // the former toISOString() was.
    const estMs = est ? new Date(est).getTime() : NaN;
    return isNaN(estMs) ? null : localDateOf(estMs);
  };

  // Needs panel: how many slots are needed (= matches) vs. how many the setup books.
  // Reused both at the top and at the bottom by the time-rooms.
  // BOOKING COUNTERS: per time-room — planned slots, how many are reserved in
  // the calendar (booked) and how many are not yet reserved (free).
  const bookedKeySet = useMemo(
    () => new Set((serieBookings || []).map((b) => `${b.date}|${b.startTime}|${b.court}`)),
    [serieBookings],
  );
  const roomCounts = useMemo(() => {
    const map = {};
    const sISO = draft.bookingStart || series?.config?.seasonStart || null;
    const eISO = reserveEndISO();
    (draft.slotRooms || []).forEach((room) => {
      let planned = 0,
        booked = 0;
      if (sISO && eISO) {
        try {
          const slots = serieGenerateSlots(
            { rooms: [room], slotDuration: draft.slotDuration, startISO: sISO, endISO: eISO },
            bookingCfg,
          );
          planned = slots.length;
          booked = slots.filter((s) =>
            bookedKeySet.has(`${s.date}|${s.startTime}|${s.court}`),
          ).length;
        } catch {
          /* deviation: empty catch given a comment to satisfy no-empty; behaviour unchanged */
        }
      }
      map[room.id] = { planned, booked, free: Math.max(0, planned - booked) };
    });
    return map;
    // deviation: dependency array preserved verbatim from the monolith; disable exhaustive-deps to keep behaviour identical
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.slotRooms, draft.slotDuration, draft.bookingStart, bookingCfg, bookedKeySet, series]);
  const slotNeedPanel = () => {
    if (!isSeriespill) return null; // match/slot calculation applies to seriespill only
    const classes = series?.config?.classes || [];
    const coveredIds = new Set();
    (draft.slotRooms || []).forEach((r) => (r.classIds || []).forEach((id) => coveredIds.add(id)));
    const relevantClasses = classes.filter((c) => coveredIds.has(c.id));
    const seasonMatches = relevantClasses.reduce((sum, c) => {
      const dr = c.doubleRound != null ? c.doubleRound : draft.doubleRound;
      const factor = dr ? 2 : 1;
      return (
        sum +
        (c.divisions || []).reduce((s, d) => {
          const n = (d.pairs || []).length;
          return s + ((n * (n - 1)) / 2) * factor;
        }, 0)
      );
    }, 0);
    const startISO = draft.bookingStart || series?.config?.seasonStart || null;
    const endISO = reserveEndISO();
    // deviation: hasRecurring -> _hasRecurring (assigned but unused in this range) to satisfy no-unused-vars; behaviour unchanged
    let plannedSlots = 0,
      _hasRecurring = false;
    if (startISO && endISO) {
      (draft.slotRooms || []).forEach((room) => {
        let dates;
        if (!isSeriespill || room.recurring === false) {
          dates = room.singleDate ? [room.singleDate] : [];
        } else {
          _hasRecurring = true;
          const freq = room.recurFreq || 'weekly';
          const interval = Math.max(1, Number(room.recurInterval) || 1);
          const man0 = (room.weekdays || []).map((d) => (Number(d) + 6) % 7);
          const re = room.recurEnd || 'until';
          let endType = 'until',
            count,
            until = endISO;
          if (re === 'count') {
            endType = 'count';
            count = Math.max(1, Number(room.recurCount) || 1);
          } else if (re === 'until') {
            until = room.recurUntil || endISO;
          }
          dates = expandRecurrence(startISO, {
            freq,
            interval,
            weekdays: freq === 'weekly' ? man0 : [],
            endType,
            count,
            until,
          });
        }
        if (isSeriespill && (room.holidayMode || 'skip') === 'skip' && bookingCfg)
          dates = dates.filter((iso) => !isHoliday(iso, bookingCfg));
        plannedSlots +=
          dates.length * Math.max(1, Number(room.slotCount) || 1) * (room.courtList || []).length;
      });
    }
    if (seasonMatches === 0 && plannedSlots === 0) return null;
    // deviation: type-only (monolith is @ts-nocheck) — Date subtraction; cast operands to preserve exact values.
    const months =
      startISO && endISO
        ? Math.max(
            1,
            Math.round(
              ((new Date(endISO + 'T00:00:00') as any) -
                (new Date(startISO + 'T00:00:00') as any)) /
                (1000 * 60 * 60 * 24 * 30.4),
            ),
          )
        : 0;
    const perMonth = months > 0 ? Math.round(plannedSlots / months) : 0;
    const diff = plannedSlots - seasonMatches;
    // Fixed pairs: each slot = 1 match = 2 pairs play. Per player = per pair (always plays with a partner).
    const totalPairs = relevantClasses.reduce(
      (s, c) => s + (c.divisions || []).reduce((a, d) => a + (d.pairs || []).length, 0),
      0,
    );
    const playsPerMonth =
      totalPairs > 0 && months > 0 ? (plannedSlots * 2) / totalPairs / months : 0;
    const fmtPlays =
      playsPerMonth > 0
        ? playsPerMonth >= 10
          ? Math.round(playsPerMonth)
          : playsPerMonth.toFixed(1)
        : '0';
    return (
      <div className="mt-2 p-2.5 rounded-lg" style={{ background: 'var(--lime-bg)' }}>
        {playsPerMonth > 0 && (
          <div className="text-[12px] text-ink-1">
            {t('seriespill.booking.playsLead')}{' '}
            <span className="font-semibold" style={{ color: 'var(--lime-text)' }}>
              ≈ {fmtPlays}
            </span>
            {t(
              playsPerMonth >= 1.5 || playsPerMonth < 1
                ? 'seriespill.booking.playsPerMonthMany'
                : 'seriespill.booking.playsPerMonthOne',
            )}{' '}
            <span className="text-ink-3">
              {t('seriespill.booking.playsDetail', {
                players: String(totalPairs * 2),
                slots: String(perMonth),
              })}
            </span>
            .
          </div>
        )}
        <div className="text-[12px] text-ink-2 mt-0.5">
          {t('seriespill.booking.seasonMatchesLead', { count: String(seasonMatches) })}
          {relevantClasses.length
            ? t('seriespill.booking.seasonMatchesClasses', {
                classes: relevantClasses.map((c) => c.name).join(', '),
              })
            : ''}
          {t('seriespill.booking.seasonMatchesNeed', { count: String(seasonMatches) })}{' '}
          <span className="font-semibold">{plannedSlots}</span>.
        </div>
        {seasonMatches > 0 && diff > 0 && (
          <div className="text-[11px] mt-1" style={{ color: '#b45309' }}>
            {t('seriespill.booking.slotsSurplus', { count: String(diff) })}
          </div>
        )}
        {seasonMatches > 0 && diff < 0 && (
          <div className="text-[11px] mt-1" style={{ color: '#b45309' }}>
            {t('seriespill.booking.slotsShort', { count: String(-diff) })}
          </div>
        )}
        {seasonMatches > 0 && diff === 0 && (
          <div className="text-[11px] mt-1" style={{ color: 'var(--lime-text)' }}>
            {t('seriespill.booking.slotsExact')}
          </div>
        )}
      </div>
    );
  };

  const previewReservations = async () => {
    setReserveResult(null);
    setReserveBusy(true);
    try {
      const all = await fetchBookings();
      // Exclude this series' own bookings (all occurrences, incl. the _r suffix)
      // — reserving REPLACES them and cleans up old/mis-dated slots.
      const seriesPrefix = series.id + '_r';
      const existing = all.filter(
        (b) => b.seriesId !== series.id && !String(b.seriesId || '').startsWith(seriesPrefix),
      );
      let bufferMin = BOOKING_DEFAULT_BUFFER_MIN;
      let holidayCfg = null;
      try {
        const cr = await Storage.get('booking_config_v1');
        if (cr?.value) {
          const c = JSON.parse(cr.value);
          if (c) {
            if (typeof c.bufferMin === 'number') bufferMin = c.bufferMin;
            holidayCfg = c;
          }
        }
      } catch {
        /* deviation: empty catch given a comment to satisfy no-empty; behaviour unchanged */
      }
      const startISO = draft.bookingStart || series?.config?.seasonStart || null;
      const endISO = reserveEndISO();
      if (!startISO || !endISO) {
        setReserveResult({ error: t('seriespill.booking.errNoDates') });
        setReserveBusy(false);
        return;
      }
      let rooms = (draft.slotRooms || [])
        .map((r) => (isSeriespill ? r : { ...r, recurring: false }))
        .filter((r) => {
          const dateOk =
            r.recurring === false
              ? !!r.singleDate
              : (r.recurFreq || 'weekly') === 'weekly'
                ? (r.weekdays || []).length
                : true;
          return dateOk && (r.courtList || []).length;
        });
      // RECURRING: expand to one room copy per recurrence date, so that ALL the series' dates
      // are reserved here and shown in the receipt. Each date gets the correct occurrence ID.
      const recCfg = !isSeriespill && draft.recurrence?.enabled ? draft.recurrence : null;
      const occDateIndex = {};
      if (recCfg) {
        const unit = recCfg.unit === 'month' ? 'month' : 'week';
        const interval = Math.max(1, Number(recCfg.interval) || 1);
        const mode = recCfg.mode === 'rolling' ? 'rolling' : 'count';
        const count = Math.max(1, Math.min(52, Number(recCfg.count) || 1));
        const horizon = Math.max(1, Number(recCfg.horizon) || 1);
        const hUnit = recCfg.horizonUnit === 'week' ? 'week' : 'month';
        const shift = (iso, n) => {
          const [y, m, d] = iso.split('-').map(Number);
          const dt = new Date(y, m - 1, d);
          if (unit === 'month') dt.setMonth(dt.getMonth() + n);
          else dt.setDate(dt.getDate() + n * 7);
          return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
        };
        let occN = count;
        if (mode === 'rolling') {
          const cut = new Date();
          cut.setHours(0, 0, 0, 0);
          if (hUnit === 'week') cut.setDate(cut.getDate() + horizon * 7);
          else cut.setMonth(cut.getMonth() + horizon);
          const cutISO = `${cut.getFullYear()}-${String(cut.getMonth() + 1).padStart(2, '0')}-${String(cut.getDate()).padStart(2, '0')}`;
          const base0 = rooms[0]?.singleDate || startISO;
          occN = 1;
          let i = 1;
          while (i < 60) {
            if (shift(base0, i * interval) > cutISO) break;
            occN++;
            i++;
          }
        }
        const expanded = [];
        rooms.forEach((r) => {
          for (let i = 0; i < occN; i++) {
            const dISO = shift(r.singleDate, i * interval);
            expanded.push({ ...r, id: `${r.id}__o${i}`, singleDate: dISO });
            occDateIndex[dISO] = i;
          }
        });
        rooms = expanded;
      }
      const built = serieBuildReservations(
        {
          rooms,
          slotDuration: draft.slotDuration,
          startISO,
          endISO,
          seriesId: series.id,
          seriesName: draft.name,
        },
        existing,
        bufferMin,
        isSeriespill ? holidayCfg : null,
        true,
      );
      const conflicts = built.conflicts;
      const toCreate = recCfg
        ? built.toCreate.map((b) => {
            const idx = occDateIndex[b.date];
            return idx && idx > 0 ? { ...b, seriesId: `${series.id}_r${idx}` } : b;
          })
        : built.toCreate;
      setReservePreview({ toCreate, conflicts, existing, startISO, endISO, bufferMin });
    } catch (_e) {
      // deviation: unused catch binding e -> _e for lint (no-unused-vars)
      setReserveResult({ error: t('seriespill.booking.errReadCalendar') });
    }
    setReserveBusy(false);
  };

  // Place the matches in the reserved slots (sequentially — only unplaced ones).
  const placeMatchesNow = async () => {
    if (!onPlaceMatches) return;
    setReserveBusy(true);
    setReserveResult(null);
    try {
      let slots;
      if (draft.slotMode === 'standalone') {
        // Standalone: generate slots directly from the time-rooms — no calendar, no availability check.
        const startISO = draft.bookingStart || series?.config?.seasonStart || null;
        const endISO = reserveEndISO();
        slots = serieGenerateSlots(
          { rooms: draft.slotRooms || [], slotDuration: draft.slotDuration, startISO, endISO },
          bookingCfg,
        );
        if (!slots.length) {
          setReserveResult({ error: t('seriespill.booking.errNoSlots') });
          setReserveBusy(false);
          return;
        }
      } else {
        const all = await fetchBookings();
        slots = all
          .filter((b) => b.seriesId === series.id)
          .map((b) => ({
            date: b.date,
            startTime: b.startTime,
            endTime: b.endTime,
            court: b.court,
            classIds: b.classIds || [],
          }));
        if (!slots.length) {
          setReserveResult({ error: t('seriespill.booking.errNoReserved') });
          setReserveBusy(false);
          return;
        }
      }
      const placement = serieAssignMatchesToSlots(series.matches || [], slots);
      await onPlaceMatches(placement.matches);
      const matchKeys = new Set(
        (placement.matches || [])
          .filter((m) => m.date)
          .map((m) => `${m.date}|${m.startTime}|${m.court}`),
      );
      const emptyCount = slots.filter(
        (s) => !matchKeys.has(`${s.date}|${s.startTime}|${s.court}`),
      ).length;
      setStatusReport({
        reserved: slots.length,
        conflicts: 0,
        placed: placement.placed,
        unplaced: placement.unplaced,
        empty: emptyCount,
        emptyIds: [], // place-only: no calendar bookings to free up here
        standalone: draft.slotMode === 'standalone',
        placeOnly: true,
      });
    } catch (_e) {
      // deviation: unused catch binding e -> _e for lint (no-unused-vars)
      setReserveResult({ error: t('seriespill.booking.errPlace') });
    }
    setReserveBusy(false);
  };

  // Reset all match placement (removes date/time/court from all unplayed matches).
  const clearPlacement = async () => {
    if (!onPlaceMatches) return;
    setReserveBusy(true);
    setReserveResult(null);
    try {
      const cleared = (series.matches || []).map((m) =>
        m.completed ? m : { ...m, date: null, startTime: null, endTime: null, court: null },
      );
      await onPlaceMatches(cleared);
      setReserveResult({
        placeCleared: (series.matches || []).filter((m) => !m.completed && m.date).length,
      });
    } catch (_e) {
      // deviation: unused catch binding e -> _e for lint (no-unused-vars)
      setReserveResult({ error: t('seriespill.booking.errClearPlacement') });
    }
    setReserveBusy(false);
  };

  const clearReservations = async () => {
    setReserveBusy(true);
    setReserveResult(null);
    try {
      const all = await fetchBookings();
      const before = all.length;
      const kept = all.filter((b) => b.seriesId !== series.id);
      // #245: name the series rather than writing back everything else — which
      // also deleted any booking made since `all` was read.
      if (bookingsAreServerAuthoritative()) await deleteBookingsBySeries(String(series.id));
      else await persistBookings(kept);
      onBookingsChanged?.(kept);
      setBookingList(kept);
      setSerieBookings([]);
      setSerieBookingCount(0);
      setReserveResult({ cleared: before - kept.length });
    } catch (_e) {
      // deviation: unused catch binding e -> _e for lint (no-unused-vars)
      setReserveResult({ error: t('seriespill.booking.errClearBookings') });
    }
    setReserveBusy(false);
  };

  const commitAndPlace = async (doPlace = false) => {
    if (!reservePreview) return;
    setReserveBusy(true);
    try {
      // 1. Write the bookings (same as commitReservations)
      //
      // #245: on the API backend the whole batch goes in one request, validated
      // server-side against the live calendar. `{ smartAllocation: false,
      // bufferMin: 0 }` is the adminOverride option bag, so that is what the
      // intent carries — the reservation panel is an operator surface.
      let list = [...reservePreview.existing];
      let created = 0;
      if (bookingsAreServerAuthoritative()) {
        // Without the right the batch goes under the club's rules (#949):
        // it carries no deletions, so nothing is lost, and what the rules
        // refuse shows in the status as not reserved.
        const out = await createBookings({
          bookings: reservePreview.toCreate,
          adminOverride: sendsOverride(true, overrideRight),
        });
        list = [...list, ...out.bookings];
        created = out.created;
      } else {
        for (const bk of reservePreview.toCreate) {
          const res = createBooking(bk, list, { smartAllocation: false, bufferMin: 0 });
          if (res.ok && res.booking) {
            list = [...list, res.booking];
            created++;
          }
        }
      }
      // 2. Place the matches ONLY when explicitly requested (doPlace) — seriespill does
      //    this in a separate step after registration. Tournaments only reserve.
      const serieBks = list.filter(
        (b) => b.seriesId === series.id || String(b.seriesId || '').startsWith(series.id + '_r'),
      );
      let finalMatches = series.matches || [];
      let placed = 0,
        unplaced = 0;
      if (onPlaceMatches && doPlace) {
        const slots = serieBks.map((b) => ({
          date: b.date,
          startTime: b.startTime,
          endTime: b.endTime,
          court: b.court,
          classIds: b.classIds || [],
        }));
        const placement = serieAssignMatchesToSlots(series.matches || [], slots);
        finalMatches = placement.matches;
        placed = placement.placed;
        unplaced = placement.unplaced;
      }
      // 3. Save — bookings first, then matches (storage-first). On the API
      // backend the bookings are already written by the command above.
      if (!bookingsAreServerAuthoritative()) await persistBookings(list);
      onBookingsChanged?.(list);
      if (created > 0) onReserved?.(serieBks);
      if (onPlaceMatches && doPlace) await onPlaceMatches(finalMatches);
      // 4. Find empty slots (reserved, but without a match = surplus)
      const matchKeys = new Set(
        finalMatches.filter((m) => m.date).map((m) => `${m.date}|${m.startTime}|${m.court}`),
      );
      const emptyBookings = onPlaceMatches
        ? serieBks.filter((b) => !matchKeys.has(`${b.date}|${b.startTime}|${b.court}`))
        : [];
      // 5. Update state + open the status modal
      setBookingList(list);
      setSerieBookings(serieBks);
      setSerieBookingCount(serieBks.length);
      setReservePreview(null);
      setStatusReport({
        reserved: created,
        conflicts: reservePreview.conflicts.length,
        placed,
        unplaced,
        empty: emptyBookings.length,
        emptyIds: emptyBookings.map((b) => b.id),
        placeMode: !!onPlaceMatches,
        name: series?.config?.name || draft?.name || t('seriespill.booking.reservationFallback'),
        slots: serieBks.map((b) => ({
          date: b.date,
          court: b.court,
          startTime: b.startTime,
          endTime: b.endTime,
        })),
      });
    } catch (e) {
      setReservePreview(null);
      // The server refused the override itself (the right was narrowed after
      // `/me` answered): said as that, not as a generic failure.
      setReserveResult({
        error:
          e instanceof OverrideRefusedError
            ? t('booking.override.refused')
            : t('seriespill.booking.errReserveAndPlace'),
      });
    }
    setReserveBusy(false);
  };

  // Remove the surplus (empty) bookings that were identified in the status modal.
  const removeSurplus = async () => {
    if (!statusReport?.emptyIds?.length) return;
    setReserveBusy(true);
    try {
      const all = await fetchBookings();
      const idSet = new Set(statusReport.emptyIds);
      const kept = all.filter((b) => !idSet.has(b.id));
      if (bookingsAreServerAuthoritative()) await deleteBookingsByIds([...idSet].map(String));
      else await persistBookings(kept);
      onBookingsChanged?.(kept);
      const serieBks = kept.filter((b) => b.seriesId === series.id);
      setBookingList(kept);
      setSerieBookings(serieBks);
      setSerieBookingCount(serieBks.length);
      setStatusReport((r) => ({ ...r, empty: 0, emptyIds: [], removed: idSet.size }));
    } catch (_e) {
      // deviation: unused catch binding e -> _e for lint (no-unused-vars)
      setStatusReport((r) => ({ ...r, error: t('seriespill.booking.errRemoveSurplus') }));
    }
    setReserveBusy(false);
  };

  const WD = [1, 2, 3, 4, 5, 6, 0].map((d) => ({ d, l: t(WEEKDAY_SHORT_KEYS[d]) }));
  const allClasses = series?.config?.classes || [];
  const effCourtMode = (c) =>
    c.courtMode != null ? c.courtMode : series?.config?.courtMode || 'unallocated';
  // Only classes set to allocated courts can be put in a room. The focus class
  // (the one you came here to set up) always counts as allocated.
  const allocatableClasses = allClasses.filter(
    (c) => effCourtMode(c) === 'allocated' || c.id === focusClassId,
  );
  const notAllocatable = allClasses.filter((c) => !allocatableClasses.some((a) => a.id === c.id));
  const upd = (idx, patch) =>
    setDraft((s) => ({
      ...s,
      slotRooms: s.slotRooms.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  const covered = new Set(draft.slotRooms.flatMap((r) => r.classIds || []));
  const uncovered = allocatableClasses.filter((c) => !covered.has(c.id));
  const sm = (t) => {
    const [h, m] = (t || '0:0').split(':').map(Number);
    return h * 60 + m;
  };
  return (
    <>
      <div className="card-elev p-3">
        <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-2">
          {t('seriespill.booking.heading')}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <button
            type="button"
            onClick={() => setDraft((s) => ({ ...s, slotMode: 'integrated' }))}
            className={`text-left p-3 rounded-lg border-2 ${draft.slotMode !== 'standalone' ? 'border-lime-deep bg-lime-bg/30' : 'border-line hover:border-line-soft'}`}
          >
            <div className="text-sm font-semibold text-ink-1">
              {t('seriespill.booking.modeIntegrated')}
            </div>
            <div className="text-[11px] text-ink-3">
              {t('seriespill.booking.modeIntegratedHint')}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setDraft((s) => ({ ...s, slotMode: 'standalone' }))}
            className={`text-left p-3 rounded-lg border-2 ${draft.slotMode === 'standalone' ? 'border-lime-deep bg-lime-bg/30' : 'border-line hover:border-line-soft'}`}
          >
            <div className="text-sm font-semibold text-ink-1">
              {t('seriespill.booking.modeStandalone')}
            </div>
            <div className="text-[11px] text-ink-3">
              {t('seriespill.booking.modeStandaloneHint')}
            </div>
          </button>
        </div>
        {isSeriespill && (
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[12px] text-ink-3">{t('seriespill.booking.startsFrom')}</span>
            <input
              type="date"
              className="input w-44"
              value={draft.bookingStart || ''}
              onChange={(e) => setDraft((s) => ({ ...s, bookingStart: e.target.value }))}
            />
            <span className="text-[11px] text-ink-3 italic">
              {draft.bookingStart ? '' : t('seriespill.booking.usesSeasonStart')}
            </span>
          </div>
        )}
        <p className="text-[11px] text-ink-3 italic">
          {isSeriespill
            ? t('seriespill.booking.roomsLeadSeries')
            : t('seriespill.booking.roomsLeadSingle')}
          {t('seriespill.booking.sharedDuration')}
          <span className="text-ink-1 font-medium">
            {t('seriespill.booking.minutes', { min: String(draft.slotDuration) })}
          </span>
          {t('seriespill.booking.sharedDurationTail')}
          {draft.slotMode !== 'standalone' ? t('seriespill.booking.courtNumbersMustMatch') : ''}
        </p>
        {uncovered.length > 0 && (
          <p className="text-[11px] mt-1" style={{ color: '#b45309' }}>
            {t('seriespill.booking.uncoveredClasses', {
              classes: uncovered.map((c) => c.name).join(', '),
            })}
          </p>
        )}
        {slotNeedPanel()}
        {(() => {
          // Holiday info applies to seriespill only, and only when at least one
          // time-room is recurring (holidays are irrelevant for a single date).
          if (!isSeriespill) return null;
          if (!draft.slotRooms.some((r) => r.recurring !== false)) return null;
          if (!draft.slotRooms.some((r) => r.skipHolidays !== false)) return null;
          const country = bookingCfg?.holidayCountry || 'no';
          const startISO = draft.bookingStart || series?.config?.seasonStart || null;
          const endISO = reserveEndISO();
          if (!startISO || !endISO) return null;
          let hsArr = [];
          try {
            hsArr = [...getHolidaySet(bookingCfg || { holidayCountry: country })]
              .filter((d) => d >= startISO && d <= endISO)
              .sort();
          } catch {
            hsArr = [];
          }
          return (
            <div className="mt-2 pt-2 border-t border-line">
              <div className="text-[11px] font-mono uppercase tracking-wider text-ink-3 mb-1">
                {t('seriespill.booking.holidaysSkipped', { country: country.toUpperCase() })}
              </div>
              {hsArr.length === 0 ? (
                <p className="text-[11px] text-ink-3 italic">
                  {t('seriespill.booking.noHolidays')}
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {hsArr.map((d) => {
                    const dt = new Date(d + 'T12:00:00');
                    const mnd = t(MONTH_SHORT_KEYS[dt.getMonth()]);
                    return (
                      <span
                        key={d}
                        className="text-[11px] px-2 py-0.5 rounded border border-line text-ink-2"
                        title={holidayName(d, country) || ''}
                      >
                        {t('seriespill.booking.dayMonth', {
                          day: String(dt.getDate()),
                          month: mnd,
                        })}
                        {holidayName(d, country)
                          ? t('seriespill.booking.holidayNameSuffix', {
                              name: holidayName(d, country),
                            })
                          : ''}
                      </span>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-ink-3 italic mt-1">
                {t('seriespill.booking.holidaySetNote')}
              </p>
            </div>
          );
        })()}
      </div>

      {draft.slotRooms.map((room, idx) => {
        const nc = (room.courtList || []).length;
        const open = expandedRoom === room.id;
        const count = Math.max(1, Number(room.slotCount) || 1);
        const fmt = (t) =>
          `${String(Math.floor((t % (24 * 60)) / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
        const endM = sm(room.startTime) + count * draft.slotDuration;
        const WDl = WEEKDAY_SHORT_KEYS.map((k) => t(k));
        const daysStr =
          !isSeriespill || room.recurring === false
            ? room.singleDate
              ? `${weekdayNo(room.singleDate)} ${room.singleDate}`
              : t('seriespill.booking.noDate')
            : (room.recurFreq || 'weekly') === 'weekly'
              ? ([...(room.weekdays || [])]
                  .sort((a, b) => a - b)
                  .map((d) => WDl[d])
                  .join(', ') || t('seriespill.booking.noDays')) +
                (Number(room.recurInterval) > 1
                  ? t('seriespill.booking.everyNthWeek', { n: String(room.recurInterval) })
                  : '')
              : {
                  daily: t('seriespill.booking.freqDaily'),
                  monthly: t('seriespill.booking.freqMonthly'),
                  yearly: t('seriespill.booking.freqYearly'),
                }[room.recurFreq] +
                (Number(room.recurInterval) > 1 ? ` ×${room.recurInterval}` : '');
        const clsStr =
          (room.classIds || [])
            .map((id) => allClasses.find((c) => c.id === id)?.name)
            .filter(Boolean)
            .join(', ') || t('seriespill.booking.noClasses');
        const rc = roomCounts[room.id] || { planned: 0, booked: 0, free: 0 };
        return (
          <div key={room.id} className="card-elev p-3">
            <button
              type="button"
              onClick={() => setExpandedRoom(open ? null : room.id)}
              className="w-full flex items-center justify-between gap-3 text-left"
            >
              <div className="min-w-0">
                <div className="font-semibold text-ink-1 text-sm truncate">
                  {isSeriespill
                    ? room.name || t('seriespill.booking.roomFallback', { n: String(idx + 1) })
                    : t('seriespill.booking.standardRoom')}
                </div>
                <div className="text-[11px] text-ink-3 truncate">
                  {daysStr} · {room.startTime}–
                  {isSeriespill ? fmt(endM) : room.endTime || fmt(endM)} ·{' '}
                  {isSeriespill
                    ? t('seriespill.booking.slotsByCourts', {
                        count: String(count),
                        courts: String(nc),
                      })
                    : t('seriespill.booking.courtsOnly', { courts: String(nc) })}{' '}
                  · {clsStr}
                </div>
                <div className="text-[11px] mt-0.5">
                  <span className="text-ink-2 font-medium">
                    {t('seriespill.booking.plannedSlots', { count: String(rc.planned) })}
                  </span>
                  {draft.slotMode !== 'standalone' && rc.planned > 0 && (
                    <>
                      <span className="text-ink-3"> · </span>
                      <span style={{ color: 'var(--lime-text)' }} className="font-medium">
                        {t('seriespill.booking.bookedCount', { count: String(rc.booked) })}
                      </span>
                      <span className="text-ink-3">
                        {' '}
                        {t(
                          rc.free === 1
                            ? 'seriespill.booking.freeCountOne'
                            : 'seriespill.booking.freeCountMany',
                          { count: String(rc.free) },
                        )}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <span className="text-[11px] font-mono text-ink-3 flex-shrink-0">
                {open ? t('seriespill.booking.collapse') : t('seriespill.booking.expand')}
              </span>
            </button>
            {open && (
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-2">
                  {isSeriespill ? (
                    <input
                      className="input flex-1 font-semibold"
                      value={room.name}
                      onChange={(e) => upd(idx, { name: e.target.value })}
                      placeholder={t('seriespill.booking.roomFallback', { n: String(idx + 1) })}
                    />
                  ) : (
                    <div className="flex-1 font-semibold text-ink-1 text-sm">
                      {t('seriespill.booking.standardRoom')}
                    </div>
                  )}
                  {isSeriespill && draft.slotRooms.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((s) => ({
                          ...s,
                          slotRooms: s.slotRooms.filter((_, i) => i !== idx),
                        }))
                      }
                      className="text-[11px] px-2 py-1.5 rounded border border-rose text-rose hover:bg-rose-soft/30 flex-shrink-0"
                    >
                      {t('seriespill.booking.removeRoom')}
                    </button>
                  )}
                </div>
                {isSeriespill && (
                  <label className="flex items-center gap-2 text-[13px] font-semibold text-ink-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={room.recurring !== false}
                      onChange={(e) => upd(idx, { recurring: e.target.checked })}
                    />
                    {t('seriespill.booking.recurring')}
                  </label>
                )}
                {isSeriespill && room.recurring !== false ? (
                  <>
                    <div>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 block mb-1">
                        {t('seriespill.booking.repeats')}
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { v: 'daily', l: t('seriespill.booking.freqDaily') },
                          { v: 'weekly', l: t('seriespill.booking.freqWeekly') },
                          { v: 'monthly', l: t('seriespill.booking.freqMonthly') },
                          { v: 'yearly', l: t('seriespill.booking.freqYearly') },
                        ].map(({ v, l }) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => upd(idx, { recurFreq: v })}
                            className={`px-3 py-1.5 rounded-lg border-2 text-[12px] font-medium ${(room.recurFreq || 'weekly') === v ? 'border-lime-deep bg-lime-bg/30 text-lime-deep' : 'border-line text-ink-3'}`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[12px] text-ink-3">
                        {t('seriespill.booking.every')}
                      </span>
                      <input
                        type="number"
                        min="1"
                        className="input"
                        style={{ width: '5rem' }}
                        value={room.recurInterval || 1}
                        onChange={(e) =>
                          upd(idx, { recurInterval: Math.max(1, Number(e.target.value) || 1) })
                        }
                      />
                      <span className="text-[12px] text-ink-3">
                        {
                          {
                            daily: t('seriespill.booking.unitDays'),
                            weekly: t('seriespill.booking.unitWeeks'),
                            monthly: t('seriespill.booking.unitMonths'),
                            yearly: t('seriespill.booking.unitYears'),
                          }[room.recurFreq || 'weekly']
                        }
                      </span>
                    </div>
                    {(room.recurFreq || 'weekly') === 'weekly' && (
                      <div>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 block mb-1">
                          {t('seriespill.booking.onDays')}
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {WD.map(({ d, l }) => {
                            const on = (room.weekdays || []).includes(d);
                            return (
                              <button
                                key={d}
                                type="button"
                                onClick={() =>
                                  upd(idx, {
                                    weekdays: on
                                      ? room.weekdays.filter((x) => x !== d)
                                      : [...(room.weekdays || []), d],
                                  })
                                }
                                className={`px-3 py-1.5 rounded-lg border-2 text-[12px] font-medium ${on ? 'border-lime-deep bg-lime-bg/30 text-lime-deep' : 'border-line text-ink-3'}`}
                              >
                                {l}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 block mb-1">
                        {t('seriespill.booking.endsLabel')}
                      </span>
                      <div className="flex gap-1.5 flex-wrap">
                        {[
                          { v: 'count', l: t('seriespill.booking.endAfterCount') },
                          { v: 'until', l: t('seriespill.booking.endOnDate') },
                          { v: 'never', l: t('seriespill.booking.endNever') },
                        ].map(({ v, l }) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => upd(idx, { recurEnd: v })}
                            className={`px-3 py-1.5 rounded-lg border-2 text-[12px] font-medium ${(room.recurEnd || 'until') === v ? 'border-lime-deep bg-lime-bg/30 text-lime-deep' : 'border-line text-ink-3'}`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                      {(room.recurEnd || 'until') === 'count' && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <input
                            type="number"
                            min="1"
                            className="input"
                            style={{ width: '5rem' }}
                            value={room.recurCount || 8}
                            onChange={(e) =>
                              upd(idx, { recurCount: Math.max(1, Number(e.target.value) || 1) })
                            }
                          />
                          <span className="text-[12px] text-ink-3">
                            {t('seriespill.booking.occurrences')}
                          </span>
                        </div>
                      )}
                      {(room.recurEnd || 'until') === 'until' && (
                        <input
                          type="date"
                          className="input w-44 mt-1.5"
                          value={room.recurUntil || reserveEndISO() || ''}
                          onChange={(e) => upd(idx, { recurUntil: e.target.value })}
                        />
                      )}
                      {(room.recurEnd || 'until') === 'never' && (
                        <p className="text-[11px] text-ink-3 italic mt-1">
                          {t('seriespill.booking.repeatsUntilDeadline', {
                            date: reserveEndISO() || '—',
                          })}
                        </p>
                      )}
                    </div>
                  </>
                ) : !isSeriespill ? (
                  <>
                    <div className="flex items-center gap-2 flex-wrap text-[12px]">
                      {(() => {
                        const setStart = (ns) => {
                          const ne =
                            sm(ns) < sm(room.endTime || '')
                              ? room.endTime
                              : fmt((sm(ns) + 120) % 1440);
                          upd(idx, { startTime: ns, endTime: ne });
                        };
                        const setEnd = (ne) => {
                          const ns =
                            sm(ne) > sm(room.startTime || '')
                              ? room.startTime
                              : fmt((sm(ne) - 120 + 1440) % 1440);
                          upd(idx, { endTime: ne, startTime: ns });
                        };
                        const [sh, smm] = (room.startTime || '18:00').split(':');
                        const [eh, emm] = (room.endTime || '20:00').split(':');
                        const hourSel = (val, onCh) => (
                          <select
                            className="input"
                            style={{ width: '4rem' }}
                            value={val}
                            onChange={onCh}
                          >
                            {Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0')).map(
                              (h) => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ),
                            )}
                          </select>
                        );
                        const minSel = (val, onCh) => (
                          <select
                            className="input"
                            style={{ width: '4rem' }}
                            value={val}
                            onChange={onCh}
                          >
                            {['00', '30'].map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        );
                        return (
                          <>
                            <span className="text-ink-3">{t('seriespill.booking.date')}</span>
                            <input
                              type="date"
                              className="input"
                              style={{ width: '8.5rem' }}
                              value={room.singleDate || ''}
                              onChange={(e) => upd(idx, { singleDate: e.target.value })}
                            />
                            <span className="text-ink-3 ml-1">{t('seriespill.booking.from')}</span>
                            <span className="inline-flex items-center gap-0.5">
                              {hourSel(sh, (e) => setStart(`${e.target.value}:${smm}`))}
                              <span className="text-ink-3">:</span>
                              {minSel(smm, (e) => setStart(`${sh}:${e.target.value}`))}
                            </span>
                            <span className="text-ink-3">{t('seriespill.booking.to')}</span>
                            <span className="inline-flex items-center gap-0.5">
                              {hourSel(eh, (e) => setEnd(`${e.target.value}:${emm}`))}
                              <span className="text-ink-3">:</span>
                              {minSel(emm, (e) => setEnd(`${eh}:${e.target.value}`))}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    {(() => {
                      if (!room.singleDate)
                        return (
                          <div
                            className="mt-2 text-base font-semibold"
                            style={{ color: '#b45309' }}
                          >
                            {t('seriespill.booking.pickADate')}
                          </div>
                        );
                      const dur = sm(room.endTime || room.startTime) - sm(room.startTime);
                      const months = MONTH_LONG_KEYS.map((k) => t(k));
                      const [yy, mm, dd] = room.singleDate.split('-').map(Number);
                      const wd = weekdayNo(room.singleDate);
                      const niceDate = t('seriespill.booking.niceDate', {
                        wd: wd.charAt(0).toUpperCase() + wd.slice(1),
                        day: String(dd),
                        month: months[mm - 1],
                        year: String(yy),
                      });
                      const durLbl =
                        t('seriespill.booking.durHours', { h: String(Math.floor(dur / 60)) }) +
                        (dur % 60
                          ? t('seriespill.booking.durMinutes', { m: String(dur % 60) })
                          : '');
                      return (
                        <div
                          className="mt-2 p-3 rounded-xl"
                          style={{ background: '#f7fdde', border: '1px solid #d9f0a3' }}
                        >
                          {dur <= 0 ? (
                            <div className="text-lg font-semibold" style={{ color: '#b45309' }}>
                              {t('seriespill.booking.endBeforeStart')}
                            </div>
                          ) : (
                            <div className="text-lg font-semibold text-ink-1 leading-snug">
                              {niceDate}
                              <br />
                              <span className="text-ink-2 font-medium">
                                {t('seriespill.booking.atTime', {
                                  start: room.startTime,
                                  end: room.endTime,
                                  dur: durLbl,
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {/* Place in the calendar: visual day timeline + clickable free start times.
                            Hidden in standalone — there is no calendar/availability to show there. */}
                    {draft.slotMode !== 'standalone' &&
                      room.singleDate &&
                      (() => {
                        const toMin = (t) => {
                          const [h, m] = (t || '0:0').split(':').map(Number);
                          return h * 60 + (m || 0);
                        };
                        const dur = toMin(room.endTime) - toMin(room.startTime);
                        const sel = (room.courtList || []).map((c) => c.num).filter(Boolean);
                        if (dur <= 0 || sel.length === 0) return null;
                        const RS = 7 * 60,
                          RE = 24 * 60,
                          SPAN = RE - RS;
                        const fmt2 = (m) =>
                          `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
                        const pct = (m) =>
                          `${Math.max(0, Math.min(100, ((m - RS) / SPAN) * 100))}%`;
                        const curS = toMin(room.startTime);
                        const isFree = (t) =>
                          sel.every(
                            (c) =>
                              !dayBk.some(
                                (b) =>
                                  b.court === c &&
                                  toMin(b.startTime) < t + dur &&
                                  toMin(b.endTime) > t,
                              ),
                          );
                        const starts = [];
                        for (let t = RS; t + dur <= RE; t += 30) if (isFree(t)) starts.push(t);
                        const curOk = isFree(curS);
                        const shiftDay = (iso, d) => {
                          const [y, m, dd] = iso.split('-').map(Number);
                          const dt = new Date(y, m - 1, dd + d);
                          return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
                        };
                        const rows = showAllCourts ? clubCourtList.map((c) => c.num) : sel;
                        const wd = weekdayNo(room.singleDate);
                        const [_dy, dm, dd] = room.singleDate.split('-').map(Number); // deviation: unused dy -> _dy for no-unused-vars
                        const dateLbl = `${wd.charAt(0).toUpperCase() + wd.slice(1)} ${dd}.${dm}.`;
                        const onWinDown = (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const r = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX - r.left;
                          const mode = x < 10 ? 'l' : x > r.width - 10 ? 'r' : 'move';
                          const trackW = placeTrackRef.current?.getBoundingClientRect().width || 1;
                          placeDrag.current = {
                            mode,
                            startX: e.clientX,
                            s0: toMin(room.startTime),
                            e0: toMin(room.endTime),
                            trackW,
                          };
                          try {
                            e.currentTarget.setPointerCapture(e.pointerId);
                          } catch {
                            /* deviation: empty catch given a comment to satisfy no-empty; behaviour unchanged */
                          }
                        };
                        const onWinMove = (e) => {
                          const d = placeDrag.current;
                          if (!d) return;
                          const dMin =
                            Math.round(((e.clientX - d.startX) * SPAN) / d.trackW / 30) * 30;
                          const dur0 = d.e0 - d.s0;
                          let ns = d.s0,
                            ne = d.e0;
                          if (d.mode === 'move') {
                            ns = Math.max(RS, Math.min(RE - dur0, d.s0 + dMin));
                            ne = ns + dur0;
                          } else if (d.mode === 'l') {
                            ns = Math.max(RS, Math.min(d.e0 - 30, d.s0 + dMin));
                            ne = d.e0;
                          } else {
                            ne = Math.max(d.s0 + 30, Math.min(RE, d.e0 + dMin));
                            ns = d.s0;
                          }
                          if (fmt2(ns) !== room.startTime || fmt2(ne) !== room.endTime)
                            upd(idx, { startTime: fmt2(ns), endTime: fmt2(ne) });
                        };
                        const onWinUp = (e) => {
                          placeDrag.current = null;
                          try {
                            e.currentTarget.releasePointerCapture(e.pointerId);
                          } catch {
                            /* deviation: empty catch given a comment to satisfy no-empty; behaviour unchanged */
                          }
                        };
                        const curIsWeekend = (() => {
                          const [y, m, d] = room.singleDate.split('-').map(Number);
                          const dow = new Date(y, m - 1, d).getDay();
                          return dow === 0 || dow === 6;
                        })();
                        const effMode =
                          findMode === 'auto' ? (curIsWeekend ? 'weekend' : 'weekday') : findMode;
                        const findNextFree = async () => {
                          setFindMsg('');
                          try {
                            const all = await fetchBookings();
                            const s = toMin(room.startTime),
                              dur0 = toMin(room.endTime) - toMin(room.startTime);
                            const [y, m, d] = room.singleDate.split('-').map(Number);
                            for (let i = 1; i <= 365; i++) {
                              const dt = new Date(y, m - 1, d + i);
                              const dow = dt.getDay();
                              if (effMode === 'weekday' && (dow === 0 || dow === 6)) continue;
                              if (effMode === 'weekend' && !(dow === 0 || dow === 6)) continue;
                              const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
                              const dayB = all.filter(
                                (b) => b.date === iso && b.seriesId !== series?.id,
                              );
                              const free = sel.every(
                                (c) =>
                                  !dayB.some(
                                    (b) =>
                                      b.court === c &&
                                      toMin(b.startTime) < s + dur0 &&
                                      toMin(b.endTime) > s,
                                  ),
                              );
                              if (free) {
                                upd(idx, { singleDate: iso });
                                return;
                              }
                            }
                            setFindMsg(t('seriespill.booking.noFreeDay'));
                          } catch {
                            setFindMsg(t('seriespill.booking.searchFailed'));
                          }
                        };
                        const todayISO = (() => {
                          const t = new Date();
                          return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
                        })();
                        // THE TIMELINE IS A POINTER SHORTCUT (#900). Dragging this
                        // window, or clicking the track, sets the start the
                        // «Ledige starttider» buttons below also set (and the
                        // length the time fields set), so neither is a control
                        // of its own: both are role="presentation".
                        const winRect = () => (
                          <div
                            role="presentation"
                            onPointerDown={onWinDown}
                            onPointerMove={onWinMove}
                            onPointerUp={onWinUp}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute top-0 bottom-0 rounded border-2 cursor-grab active:cursor-grabbing"
                            style={{
                              left: pct(curS),
                              width: `${(dur / SPAN) * 100}%`,
                              borderColor: curOk ? 'var(--lime-deep)' : '#dc2626',
                              background: curOk ? 'rgba(132,204,22,0.30)' : 'rgba(220,38,38,0.20)',
                              zIndex: 5,
                              touchAction: 'none',
                            }}
                            title={t('seriespill.booking.dragHint')}
                          >
                            <span
                              className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize"
                              style={{
                                background: curOk ? 'var(--lime-deep)' : '#dc2626',
                                opacity: 0.55,
                              }}
                            />
                            <span
                              className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize"
                              style={{
                                background: curOk ? 'var(--lime-deep)' : '#dc2626',
                                opacity: 0.55,
                              }}
                            />
                          </div>
                        );
                        return (
                          <div className="mt-2 p-3 rounded-xl border border-line">
                            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                                {t('seriespill.booking.placeInCalendar')}
                              </span>
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      upd(idx, { singleDate: shiftDay(room.singleDate, -1) })
                                    }
                                    title={t('seriespill.booking.prevDay')}
                                    className="px-1.5 py-0.5 rounded border border-line-strong text-ink-2 hover:border-lime-deep"
                                  >
                                    ◀
                                  </button>
                                  <span className="text-[11px] font-medium text-ink-1 min-w-[5.5rem] text-center">
                                    {dateLbl}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      upd(idx, { singleDate: shiftDay(room.singleDate, 1) })
                                    }
                                    title={t('seriespill.booking.nextDay')}
                                    className="px-1.5 py-0.5 rounded border border-line-strong text-ink-2 hover:border-lime-deep"
                                  >
                                    ▶
                                  </button>
                                  {room.singleDate !== todayISO && (
                                    <button
                                      type="button"
                                      onClick={() => upd(idx, { singleDate: todayISO })}
                                      title={t('seriespill.booking.backToToday')}
                                      className="ml-1 px-2 py-0.5 rounded border border-line-strong text-ink-2 text-[11px] hover:border-lime-deep"
                                    >
                                      {t('seriespill.booking.today')}
                                    </button>
                                  )}
                                </div>
                                <label className="flex items-center gap-1 text-[11px] text-ink-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={showAllCourts}
                                    onChange={(e) => setShowAllCourts(e.target.checked)}
                                  />{' '}
                                  {t('seriespill.booking.showAllCourts')}
                                </label>
                              </div>
                            </div>
                            <div className="space-y-0.5">
                              <div className="relative h-4">
                                {Array.from(
                                  { length: Math.floor(SPAN / 120) + 1 },
                                  (_, i) => RS + i * 120,
                                ).map((t) => (
                                  <span
                                    key={t}
                                    className="absolute text-[9px] text-ink-3 font-mono"
                                    style={{ left: pct(t), transform: 'translateX(-50%)' }}
                                  >
                                    {fmt2(t)}
                                  </span>
                                ))}
                              </div>
                              <div
                                ref={placeTrackRef}
                                role="presentation"
                                className="relative space-y-0.5 cursor-pointer"
                                onClick={(e) => {
                                  if (placeDrag.current) return;
                                  const r = placeTrackRef.current?.getBoundingClientRect();
                                  if (!r) return;
                                  const dur0 = toMin(room.endTime) - toMin(room.startTime);
                                  let t =
                                    RS +
                                    Math.round((((e.clientX - r.left) / r.width) * SPAN) / 30) * 30;
                                  t = Math.max(RS, Math.min(RE - dur0, t));
                                  upd(idx, { startTime: fmt2(t), endTime: fmt2(t + dur0) });
                                }}
                              >
                                {rows.map((c) => {
                                  const isSel = sel.includes(c);
                                  return (
                                    <div
                                      key={c}
                                      className="relative h-4 rounded overflow-hidden"
                                      style={{
                                        background: isSel
                                          ? 'rgba(132,204,22,0.10)'
                                          : 'rgba(0,0,0,0.035)',
                                        opacity: isSel ? 1 : 0.75,
                                      }}
                                      title={
                                        t('seriespill.booking.courtN', { num: String(c) }) +
                                        (isSel ? '' : t('seriespill.booking.notSelectedSuffix'))
                                      }
                                    >
                                      {dayBk
                                        .filter((b) => b.court === c)
                                        .map((b, i) => {
                                          const bs = toMin(b.startTime),
                                            be = toMin(b.endTime);
                                          return (
                                            <div
                                              key={i}
                                              className="absolute top-0 bottom-0"
                                              style={{
                                                left: pct(bs),
                                                width: `${((be - bs) / SPAN) * 100}%`,
                                                background: '#cbd5e1',
                                              }}
                                              title={`${b.startTime}–${b.endTime} · ${b.title || t('seriespill.booking.busy')}`}
                                            />
                                          );
                                        })}
                                      {isSel && winRect()}
                                      <span
                                        className="absolute left-1 top-0 text-[9px] font-mono pointer-events-none"
                                        style={{
                                          color: isSel ? 'var(--ink-2)' : 'var(--ink-3)',
                                          zIndex: 6,
                                        }}
                                      >
                                        B{c}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={findNextFree}
                                className="px-3 py-1.5 rounded-lg border text-[12px] font-medium hover:bg-lime-bg"
                                style={{
                                  borderColor: 'var(--lime-deep)',
                                  color: 'var(--lime-text)',
                                }}
                              >
                                {t('seriespill.booking.findNextFree')}
                              </button>
                              <select
                                value={effMode}
                                onChange={(e) => setFindMode(e.target.value)}
                                className="input"
                                style={{ width: '8.5rem', fontSize: '12px' }}
                              >
                                <option value="any">{t('seriespill.booking.findAnyDay')}</option>
                                <option value="weekday">
                                  {t('seriespill.booking.findWeekday')}
                                </option>
                                <option value="weekend">
                                  {t('seriespill.booking.findWeekend')}
                                </option>
                              </select>
                              {findMsg && (
                                <span className="text-[11px]" style={{ color: '#b45309' }}>
                                  {findMsg}
                                </span>
                              )}
                            </div>
                            <div className="mt-2">
                              <span className="text-[11px] text-ink-3">
                                {t('seriespill.booking.freeStartTimes')}
                              </span>
                              <div className="flex flex-wrap gap-1 mt-1 max-h-24 overflow-y-auto">
                                {starts.length === 0 ? (
                                  <span className="text-[11px] italic" style={{ color: '#b45309' }}>
                                    {t(
                                      sel.length === 1
                                        ? 'seriespill.booking.noFreeSlotsOne'
                                        : 'seriespill.booking.noFreeSlotsMany',
                                      { count: String(sel.length) },
                                    )}
                                  </span>
                                ) : (
                                  starts.map((t) => (
                                    <button
                                      key={t}
                                      type="button"
                                      onClick={() =>
                                        upd(idx, { startTime: fmt2(t), endTime: fmt2(t + dur) })
                                      }
                                      className={`px-2 py-1 rounded-md text-[11px] font-mono border ${t === curS ? 'border-lime-deep bg-lime-bg text-lime-deep font-semibold' : 'border-line text-ink-2 hover:border-lime-deep'}`}
                                    >
                                      {fmt2(t)}
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                  </>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] text-ink-3">{t('seriespill.booking.date')}</span>
                    <input
                      type="date"
                      className="input w-44"
                      value={room.singleDate || ''}
                      onChange={(e) => upd(idx, { singleDate: e.target.value })}
                    />
                  </div>
                )}
                {isSeriespill && room.recurring !== false && (
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 block mb-1">
                      {t('seriespill.booking.holidayLanding')}
                    </span>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => upd(idx, { holidayMode: 'include' })}
                        className={`flex-1 px-3 py-1.5 rounded-lg border-2 text-[12px] font-medium ${(room.holidayMode || 'skip') === 'include' ? 'border-lime-deep bg-lime-bg/30 text-lime-deep' : 'border-line text-ink-3'}`}
                      >
                        {t('seriespill.booking.holidayInclude')}
                      </button>
                      <button
                        type="button"
                        onClick={() => upd(idx, { holidayMode: 'skip' })}
                        className={`flex-1 px-3 py-1.5 rounded-lg border-2 text-[12px] font-medium ${(room.holidayMode || 'skip') === 'skip' ? 'border-lime-deep bg-lime-bg/30 text-lime-deep' : 'border-line text-ink-3'}`}
                      >
                        {t('seriespill.booking.holidaySkip')}
                      </button>
                    </div>
                    <p className="text-[10px] text-ink-3 italic mt-1">
                      {t('seriespill.booking.holidaySet', {
                        country: (bookingCfg?.holidayCountry || 'no').toUpperCase(),
                      })}
                    </p>
                  </div>
                )}
                {isSeriespill && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] text-ink-3">
                      {t('seriespill.booking.startTime')}
                    </span>
                    <input
                      type="time"
                      className="input w-28"
                      value={room.startTime}
                      onChange={(e) => upd(idx, { startTime: e.target.value })}
                    />
                    <span className="text-[12px] text-ink-3 ml-2">
                      {t('seriespill.booking.slotCount')}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        upd(idx, { slotCount: Math.max(1, (Number(room.slotCount) || 1) - 1) })
                      }
                      aria-label={t('seriespill.booking.slotCountDecrease')}
                      className="px-2.5 py-1 rounded-lg border border-line text-ink-2"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      className="input"
                      style={{ width: '4rem' }}
                      value={room.slotCount}
                      onChange={(e) =>
                        upd(idx, { slotCount: Math.max(1, Number(e.target.value) || 1) })
                      }
                    />
                    <button
                      type="button"
                      onClick={() => upd(idx, { slotCount: (Number(room.slotCount) || 1) + 1 })}
                      aria-label={t('seriespill.booking.slotCountIncrease')}
                      className="px-2.5 py-1 rounded-lg border border-line text-ink-2"
                    >
                      +
                    </button>
                    <span className="text-[12px] text-ink-3 ml-2">
                      {t('seriespill.booking.end')}
                    </span>
                    <input
                      type="time"
                      className="input w-28"
                      value={fmt(endM % (24 * 60))}
                      onChange={(e) => {
                        const [h, m] = (e.target.value || '0:0').split(':').map(Number);
                        let endNew = h * 60 + m;
                        const st = sm(room.startTime);
                        if (endNew <= st) endNew += 24 * 60;
                        upd(idx, {
                          slotCount: Math.max(1, Math.round((endNew - st) / draft.slotDuration)),
                        });
                      }}
                    />
                    <span className="text-[11px] text-ink-3">
                      {t('seriespill.booking.slotDurationNote', {
                        min: String(draft.slotDuration),
                      })}
                    </span>
                  </div>
                )}
                {draft.slotMode === 'standalone' ? (
                  <div className="space-y-3">
                    <div>
                      <span className="text-[12px] text-ink-3 block mb-1.5">
                        {t('seriespill.booking.courtCount')}
                      </span>
                      <select
                        className="input"
                        style={{ width: '9rem' }}
                        value={(room.courtList || []).length || 1}
                        onChange={(e) => {
                          const n = Math.max(1, Number(e.target.value) || 1);
                          upd(idx, {
                            courtList: Array.from({ length: n }, (_, i) => ({
                              id: `crt_${idx}_${i + 1}`,
                              num: i + 1,
                              name: '',
                            })),
                          });
                        }}
                      >
                        {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>
                            {t(
                              n === 1
                                ? 'seriespill.booking.courtCountOne'
                                : 'seriespill.booking.courtCountMany',
                              { count: String(n) },
                            )}
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-ink-3 mt-1">
                        {t('seriespill.booking.courtCountHint')}
                      </p>
                    </div>
                    <div>
                      <span className="text-[12px] text-ink-3 block mb-1.5">
                        {t('seriespill.booking.whereLabel')}
                      </span>
                      <input
                        className="input w-full text-sm mb-1.5"
                        placeholder={t('seriespill.booking.venuePlaceholder')}
                        value={draft.venueName || ''}
                        onChange={(e) => setDraft((s) => ({ ...s, venueName: e.target.value }))}
                      />
                      <div className="relative">
                        <input
                          className="input w-full text-sm"
                          placeholder={t('seriespill.booking.addressPlaceholder')}
                          value={draft.venueAddress || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setDraft((s) => ({ ...s, venueAddress: v }));
                            searchGeonorge(v);
                          }}
                          onFocus={() => {
                            if (addrResults.length) setAddrOpen(true);
                          }}
                          onBlur={() => setTimeout(() => setAddrOpen(false), 150)}
                        />
                        {addrLoading && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-ink-3">
                            {t('seriespill.booking.searching')}
                          </span>
                        )}
                        {addrOpen && addrResults.length > 0 && (
                          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-line rounded-lg shadow-lg max-h-56 overflow-auto">
                            {addrResults.map((a, i) => (
                              <button
                                key={i}
                                type="button"
                                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-line last:border-b-0"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setDraft((s) => ({
                                    ...s,
                                    venueAddress: a.tekst,
                                    venuePostnr: a.postnr,
                                    venuePoststed: a.poststed,
                                  }));
                                  setAddrOpen(false);
                                  setAddrResults([]);
                                }}
                              >
                                <span className="text-ink-1">{a.tekst}</span>
                                {(a.postnr || a.poststed) && (
                                  <span className="text-ink-3">
                                    {' '}
                                    · {[a.postnr, a.poststed].filter(Boolean).join(' ')}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {(draft.venuePostnr || draft.venuePoststed) && (
                        <p className="text-[11px] text-ink-2 mt-1 flex items-center gap-1">
                          <MapPin size={12} className="text-ink-3" />
                          {[draft.venuePostnr, draft.venuePoststed].filter(Boolean).join(' ')}
                        </p>
                      )}
                      <p className="text-[10px] text-ink-3 mt-1">
                        {t('seriespill.booking.addressHint')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <span className="text-[12px] text-ink-3 block mb-1.5">
                      {t('seriespill.booking.courtsLabel')}
                    </span>
                    {clubCourtList.length > 0 ? (
                      <>
                        <div className="flex flex-wrap gap-1.5">
                          {clubCourtList.map((c) => {
                            const on = (room.courtList || []).some((x) => Number(x.num) === c.num);
                            const av =
                              !isSeriespill && courtAvail[c.num] ? courtAvail[c.num].status : null;
                            const toggle = () =>
                              upd(idx, {
                                courtList: on
                                  ? room.courtList.filter((x) => Number(x.num) !== c.num)
                                  : [
                                      ...(room.courtList || []),
                                      { id: `crt_${c.num}`, num: c.num, name: c.name },
                                    ],
                              });
                            if (!av) {
                              // Seriespill / unknown availability: original lime/line style.
                              return (
                                <button
                                  key={c.num}
                                  type="button"
                                  onClick={toggle}
                                  className={`px-3 py-1.5 rounded-lg border-2 text-[12px] font-medium ${on ? 'border-lime-deep bg-lime-bg/30 text-lime-deep' : 'border-line text-ink-3'}`}
                                >
                                  {c.name}
                                </button>
                              );
                            }
                            const palette = {
                              green: { border: '#84cc16', bg: '#f7fdde', text: '#3f6212' },
                              yellow: { border: '#d97706', bg: '#fef3c7', text: '#92400e' },
                              red: { border: '#dc2626', bg: '#fee2e2', text: '#b91c1c' },
                            }[av];
                            return (
                              <button
                                key={c.num}
                                type="button"
                                onClick={toggle}
                                title={
                                  av === 'red'
                                    ? t('seriespill.booking.courtBusyTitle')
                                    : av === 'yellow'
                                      ? (courtAvail[c.num].gaps || [])
                                          .map((g) =>
                                            t('seriespill.booking.gapTitle', {
                                              mins: String(g.mins),
                                              from: g.from,
                                              to: g.to,
                                            }),
                                          )
                                          .join(', ') || t('seriespill.booking.courtWasteTitle')
                                      : t('seriespill.booking.courtFreeTitle')
                                }
                                className="px-3 py-1.5 rounded-lg border-2 text-[12px] font-medium inline-flex items-center gap-1"
                                style={{
                                  borderColor: palette.border,
                                  background: on ? palette.bg : '#fff',
                                  color: palette.text,
                                }}
                              >
                                {on && <span style={{ fontWeight: 700 }}>✓</span>}
                                {c.name}
                                {av === 'red' && (
                                  <span style={{ fontSize: '10px', opacity: 0.8 }}>
                                    {t('seriespill.booking.busySuffix')}
                                  </span>
                                )}
                                {av === 'yellow' && (
                                  <span style={{ fontSize: '10px', opacity: 0.8 }}>· ⚠</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {isSeriespill ? (
                          <p className="text-[10px] text-ink-3 italic mt-1">
                            {t('seriespill.booking.pickClubCourts')}
                          </p>
                        ) : (
                          (() => {
                            const smartOn = bookingCfg?.smartAllocation !== false;
                            const bufMin = bookingCfg?.bufferMin ?? BOOKING_DEFAULT_BUFFER_MIN;
                            return (
                              <p className="text-[10px] text-ink-3 mt-1">
                                <span style={{ color: '#3f6212' }}>
                                  {t('seriespill.booking.legendGreen')}
                                </span>
                                {' · '}
                                {smartOn && (
                                  <>
                                    <span style={{ color: '#92400e' }}>
                                      {t('seriespill.booking.legendYellow')}
                                    </span>
                                    {' · '}
                                  </>
                                )}
                                <span style={{ color: '#b91c1c' }}>
                                  {t('seriespill.booking.legendRed')}
                                </span>
                                <span className="block mt-0.5 text-ink-4">
                                  {t('seriespill.booking.smartBooking')}{' '}
                                  <span
                                    className="font-semibold"
                                    style={{ color: smartOn ? '#3f6212' : '#92400e' }}
                                  >
                                    {smartOn
                                      ? t('seriespill.booking.smartOn')
                                      : t('seriespill.booking.smartOff')}
                                  </span>
                                  {smartOn
                                    ? t('seriespill.booking.smartDetail', {
                                        buffer: String(bufMin),
                                        durations: allowedDursCfg.join('/'),
                                      })
                                    : t('seriespill.booking.smartOffDetail')}
                                </span>
                                {(() => {
                                  const reds = clubCourtList.filter(
                                    (c) =>
                                      courtAvail[c.num]?.status === 'red' &&
                                      (room.courtList || []).some((x) => Number(x.num) === c.num),
                                  );
                                  return reds.length ? (
                                    <span className="block mt-0.5" style={{ color: '#b91c1c' }}>
                                      {t('seriespill.booking.redSelected', {
                                        courts: reds.map((c) => c.name).join(', '),
                                      })}
                                    </span>
                                  ) : null;
                                })()}
                              </p>
                            );
                          })()
                        )}
                        {!isSeriespill &&
                          (() => {
                            const forced = clubCourtList.filter(
                              (c) =>
                                courtAvail[c.num]?.forced &&
                                (room.courtList || []).some((x) => Number(x.num) === c.num),
                            );
                            return forced.length ? (
                              <p className="text-[10px] mt-1.5" style={{ color: '#3f6212' }}>
                                {t('seriespill.booking.forcedCourts', {
                                  courts: forced.map((c) => c.name).join(', '),
                                })}
                              </p>
                            ) : null;
                          })()}
                        {!isSeriespill &&
                          (() => {
                            // Explain the consequence of selected YELLOW courts (what smartbooking would have stopped).
                            const yellows = clubCourtList.filter(
                              (c) =>
                                courtAvail[c.num]?.status === 'yellow' &&
                                (room.courtList || []).some((x) => Number(x.num) === c.num),
                            );
                            if (!yellows.length) return null;
                            return (
                              <div
                                className="mt-2 p-2.5 rounded-lg text-[11px]"
                                style={{
                                  background: '#fef9e7',
                                  border: '1px solid #fde68a',
                                  color: '#92400e',
                                }}
                              >
                                <div className="font-semibold mb-1">
                                  {t('seriespill.booking.yellowConsequence')}
                                </div>
                                {yellows.map((c) => {
                                  const a = courtAvail[c.num];
                                  const gapTxt =
                                    a.gaps && a.gaps.length
                                      ? a.gaps
                                          .map(
                                            (g) =>
                                              t('seriespill.booking.wastedGap', {
                                                mins: String(g.mins),
                                                from: g.from,
                                                to: g.to,
                                              }) +
                                              (g.dead ? t('seriespill.booking.wastedGapDead') : ''),
                                          )
                                          .join('; ')
                                      : null;
                                  return (
                                    <div key={c.num} className="mt-0.5">
                                      <span className="font-medium">{c.name}:</span>{' '}
                                      {gapTxt
                                        ? t('seriespill.booking.resultLeaves', { gaps: gapTxt })
                                        : t('seriespill.booking.resultIsolated')}
                                    </div>
                                  );
                                })}
                                <div className="mt-1 italic" style={{ opacity: 0.85 }}>
                                  {t('seriespill.booking.adminAllowed')}
                                </div>
                              </div>
                            );
                          })()}
                      </>
                    ) : (
                      <div className="space-y-1.5">
                        {(room.courtList || []).map((crt, ci) => (
                          <div key={crt.id} className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              className="input"
                              style={{ width: '4.5rem', flex: 'none' }}
                              value={crt.num}
                              onChange={(e) =>
                                upd(idx, {
                                  courtList: room.courtList.map((c, j) =>
                                    j === ci ? { ...c, num: e.target.value } : c,
                                  ),
                                })
                              }
                            />
                            <input
                              className="input"
                              style={{ flex: '1 1 0%', minWidth: 0 }}
                              placeholder={t('seriespill.booking.courtNamePlaceholder', {
                                num: String(crt.num || ci + 1),
                              })}
                              value={crt.name}
                              onChange={(e) =>
                                upd(idx, {
                                  courtList: room.courtList.map((c, j) =>
                                    j === ci ? { ...c, name: e.target.value } : c,
                                  ),
                                })
                              }
                            />
                            <button
                              type="button"
                              onClick={() =>
                                upd(idx, { courtList: room.courtList.filter((_, j) => j !== ci) })
                              }
                              aria-label={t('seriespill.booking.removeCourt', {
                                num: String(crt.num || ci + 1),
                              })}
                              className="text-[11px] px-2 py-1.5 rounded border border-rose text-rose hover:bg-rose-soft/30"
                              style={{ flex: 'none' }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const next =
                              Math.max(
                                0,
                                ...(room.courtList || []).map((c) => Number(c.num) || 0),
                              ) + 1;
                            upd(idx, {
                              courtList: [
                                ...(room.courtList || []),
                                { id: `crt_${idx}_${Date.now()}`, num: next, name: '' },
                              ],
                            });
                          }}
                          className="text-[11px] px-3 py-1.5 rounded-lg border border-dashed border-line text-ink-3 hover:border-lime-deep hover:text-lime-deep"
                        >
                          {t('seriespill.booking.addCourt')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {isSeriespill && (
                  <div>
                    <span className="text-[12px] text-ink-3 block mb-1.5">
                      {t('seriespill.booking.appliesToClasses')}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {allocatableClasses.map((c) => {
                        const on = (room.classIds || []).includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() =>
                              upd(idx, {
                                classIds: on
                                  ? room.classIds.filter((x) => x !== c.id)
                                  : [...(room.classIds || []), c.id],
                              })
                            }
                            className={`px-3 py-1.5 rounded-lg border-2 text-[12px] font-medium ${on ? 'border-lime-deep bg-lime-bg/30 text-lime-deep' : 'border-line text-ink-3'}`}
                          >
                            {on ? '✓ ' : ''}
                            {c.name}
                          </button>
                        );
                      })}
                      {allocatableClasses.length === 0 && (
                        <span className="text-[11px] text-ink-3 italic">
                          {t('seriespill.booking.noAllocatedClasses')}
                        </span>
                      )}
                    </div>
                    {notAllocatable.length > 0 && (
                      <p className="text-[10px] text-ink-3 mt-1.5 italic">
                        {t('seriespill.booking.notAllocatable', {
                          classes: notAllocatable.map((c) => c.name).join(', '),
                        })}
                      </p>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-ink-3 italic">
                  {(() => {
                    if (!isSeriespill) return null; // mini-tournament is summarized right below the pickers
                    if (
                      room.recurring !== false &&
                      (room.recurFreq || 'weekly') === 'weekly' &&
                      (room.weekdays || []).length === 0
                    )
                      return t('seriespill.booking.pickADay');
                    if (room.recurring === false && !room.singleDate)
                      return t('seriespill.booking.pickADateDot');
                    const start = sm(room.startTime);
                    const dur = draft.slotDuration;
                    const count = Math.max(1, Number(room.slotCount) || 1);
                    const fmt = (t) =>
                      `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
                    const times = Array.from({ length: count }, (_, k) => fmt(start + k * dur));
                    const endMin = start + count * dur;
                    const slotsPerWeek = count * nc * (room.weekdays || []).length;
                    const coveredCls = allClasses.filter((c) =>
                      (room.classIds || []).includes(c.id),
                    );
                    const matchesPerRound = coveredCls.reduce(
                      (sum, c) =>
                        sum +
                        (c.divisions || []).reduce(
                          (s, d) => s + Math.floor((d.pairs || []).length / 2),
                          0,
                        ),
                      0,
                    );
                    const weeks =
                      slotsPerWeek > 0 && matchesPerRound > 0
                        ? Math.ceil(matchesPerRound / slotsPerWeek)
                        : 0;
                    return (
                      <>
                        <span>
                          {t(
                            count * nc === 1
                              ? 'seriespill.booking.roomSummaryOne'
                              : 'seriespill.booking.roomSummaryMany',
                            {
                              slots: String(count),
                              courts: String(nc),
                              matches: String(count * nc),
                              start: room.startTime,
                              end: fmt(endMin % (24 * 60)),
                              times: times.join(', '),
                            },
                          )}
                        </span>
                        {endMin > 24 * 60 && (
                          <span style={{ color: '#b45309' }}>
                            {' '}
                            {t('seriespill.booking.pastMidnight')}
                          </span>
                        )}
                        {coveredCls.length > 0 && matchesPerRound > 0 && (
                          <span className="block mt-1 not-italic text-ink-2">
                            {t('seriespill.booking.oneRoundFor', {
                              classes: coveredCls.map((c) => c.name).join(', '),
                              matches: String(matchesPerRound),
                              slots: String(slotsPerWeek),
                            })}{' '}
                            <span className="font-semibold text-ink-1">
                              {t(
                                weeks === 1
                                  ? 'seriespill.booking.weeksBetweenOne'
                                  : 'seriespill.booking.weeksBetweenMany',
                                { weeks: String(weeks) },
                              )}
                            </span>
                            .
                          </span>
                        )}
                        {coveredCls.length > 0 && matchesPerRound === 0 && (
                          <span className="block mt-1 not-italic">
                            {t('seriespill.booking.noPairsYet')}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </p>
              </div>
            )}
          </div>
        );
      })}

      {isSeriespill && (
        <button
          type="button"
          onClick={() => {
            const id = `room_${Date.now()}`;
            setDraft((s) => ({
              ...s,
              slotRooms: [
                ...s.slotRooms,
                {
                  id,
                  name: `Tidsrom ${s.slotRooms.length + 1}`,
                  weekdays: [],
                  startTime: '18:00',
                  slotCount: 3,
                  courtList: [
                    { id: `crt_${Date.now()}`, num: 1, name: '' },
                    { id: `crt_${Date.now() + 1}`, num: 2, name: '' },
                  ],
                  classIds: [],
                },
              ],
            }));
            setExpandedRoom(id);
          }}
          className="w-full py-2.5 rounded-lg border-2 border-dashed border-line text-[12px] font-mono uppercase tracking-wider text-ink-3 hover:border-lime-deep hover:text-lime-deep transition-colors"
        >
          {t('seriespill.booking.addRoom')}
        </button>
      )}

      {recurrenceSlot}

      {/* RESERVE / PLACE — below the time-rooms/courts. Reserving is step 1
                    (both). Placing matches is a separate step (seriespill only,
                    typically after registration). Tournaments only reserve courts. */}
      {draft.slotMode !== 'standalone' ? (
        <div className="mt-3 pt-3 border-t border-line">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              id="activity-reserve-btn"
              disabled={reserveBusy}
              onClick={previewReservations}
              className="text-[12px] px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50"
              style={{
                background:
                  serieReservedState === 'mismatch'
                    ? '#f59e0b'
                    : serieReservedState === 'reserved'
                      ? '#3f6212'
                      : 'var(--lime-deep)',
                color: '#fff',
              }}
            >
              {reserveBusy
                ? t('seriespill.booking.checkingCalendar')
                : serieReservedState === 'reserved'
                  ? t(
                      serieBookingCount === 1
                        ? 'seriespill.booking.reservedOne'
                        : 'seriespill.booking.reservedMany',
                      { count: String(serieBookingCount) },
                    )
                  : serieReservedState === 'mismatch'
                    ? t('seriespill.booking.updateReservation')
                    : t('seriespill.booking.reserveCourts')}
            </button>
            {onPlaceMatches && (
              <button
                type="button"
                disabled={reserveBusy}
                onClick={placeMatchesNow}
                className="text-[12px] px-3 py-1.5 rounded-lg border-2 font-semibold disabled:opacity-50"
                style={{ borderColor: 'var(--lime-deep)', color: 'var(--lime-text)' }}
              >
                {reserveBusy
                  ? t('seriespill.booking.placing')
                  : t('seriespill.booking.placeMatches')}
              </button>
            )}
            {onPlaceMatches && placedMatchCount > 0 && (
              <button
                type="button"
                disabled={reserveBusy}
                onClick={() => setConfirmRemove('placement')}
                className="text-[12px] px-3 py-1.5 rounded-lg border border-rose text-rose hover:bg-rose-soft/30 disabled:opacity-50"
              >
                {t('seriespill.booking.clearPlacement')}
              </button>
            )}
            {serieBookingCount > 0 && (
              <button
                type="button"
                disabled={reserveBusy}
                onClick={() => setConfirmRemove('reservations')}
                className="text-[12px] px-3 py-1.5 rounded-lg border border-rose text-rose hover:bg-rose-soft/30 disabled:opacity-50"
              >
                {t('seriespill.booking.clearBookings')}
              </button>
            )}
          </div>
          <p className="text-[11px] text-ink-3 mt-1.5">
            {onPlaceMatches
              ? t('seriespill.booking.reserveHelpWithPlace')
              : t('seriespill.booking.reserveHelp')}
          </p>
          {reserveResult &&
            (reserveResult.error ? (
              <p className="text-[11px] mt-1.5" style={{ color: '#dc2626' }}>
                {reserveResult.error}
              </p>
            ) : reserveResult.placeCleared != null ? (
              <p className="text-[11px] mt-1.5 text-ink-2">
                {t('seriespill.booking.clearedPlacement', {
                  count: String(reserveResult.placeCleared),
                })}
              </p>
            ) : reserveResult.cleared != null ? (
              <p className="text-[11px] mt-1.5 text-ink-2">
                {t('seriespill.booking.clearedBookings', {
                  count: String(reserveResult.cleared),
                })}
              </p>
            ) : reserveResult.place ? (
              <p className="text-[11px] mt-1.5 text-ink-2">
                {t('seriespill.booking.placedResult', {
                  count: String(reserveResult.place.placed),
                })}
                {reserveResult.place.unplaced
                  ? t('seriespill.booking.placedMissing', {
                      count: String(reserveResult.place.unplaced),
                    })
                  : ''}
                .
              </p>
            ) : null)}
        </div>
      ) : (
        onPlaceMatches && (
          <div className="mt-3 pt-3 border-t border-line">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={reserveBusy}
                onClick={placeMatchesNow}
                className="text-[12px] px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50"
                style={{ background: 'var(--lime-deep)', color: '#fff' }}
              >
                {reserveBusy
                  ? t('seriespill.booking.placing')
                  : t('seriespill.booking.placeMatches')}
              </button>
              {placedMatchCount > 0 && (
                <button
                  type="button"
                  disabled={reserveBusy}
                  onClick={() => setConfirmRemove('placement')}
                  className="text-[12px] px-3 py-1.5 rounded-lg border border-rose text-rose hover:bg-rose-soft/30 disabled:opacity-50"
                >
                  {t('seriespill.booking.clearPlacement')}
                </button>
              )}
            </div>
            <p className="text-[11px] text-ink-3 mt-1.5">
              {t('seriespill.booking.standaloneHelp')}
            </p>
            {reserveResult &&
              (reserveResult.error ? (
                <p className="text-[11px] mt-1.5" style={{ color: '#dc2626' }}>
                  {reserveResult.error}
                </p>
              ) : reserveResult.place ? (
                <p className="text-[11px] mt-1.5 text-ink-2">
                  {t('seriespill.booking.placedResult', {
                    count: String(reserveResult.place.placed),
                  })}
                  {reserveResult.place.unplaced
                    ? t('seriespill.booking.placedMissing', {
                        count: String(reserveResult.place.unplaced),
                      })
                    : ''}
                  .
                </p>
              ) : null)}
          </div>
        )
      )}

      {confirmRemove &&
        (() => {
          const isPlacement = confirmRemove === 'placement';
          const dates = (serieBookings || [])
            .map((b) => b.date)
            .filter(Boolean)
            .sort();
          const span = dates.length ? `${dates[0]} → ${dates[dates.length - 1]}` : null;
          // A `Dialog` (#900). Escape and the backdrop answer «Avbryt», as the
          // backdrop always did, and focus starts there — the first control —
          // so a stray Enter never clears the season's bookings.
          return (
            <Dialog
              labelledBy={dialogIds.confirmTitle}
              onClose={() => setConfirmRemove(null)}
              showClose={false}
              backdropClassName=""
              backdropStyle={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                background: 'rgba(15,22,17,0.6)',
                backdropFilter: 'blur(4px)',
              }}
              className="overflow-x-hidden overflow-y-auto max-h-[92vh]"
              style={{
                maxHeight: 'calc(100dvh - 2rem)',
                background: '#fff',
                maxWidth: 420,
                width: '100%',
                borderRadius: 16,
                boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
                border: '1px solid #e7eae3',
              }}
            >
              <div
                style={{
                  padding: '14px 18px',
                  borderBottom: '1px solid #e7eae3',
                  background: '#fee2e2',
                }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      background: '#fecaca',
                      color: '#b91c1c',
                    }}
                  >
                    <AlertCircle size={16} strokeWidth={2.5} />
                  </span>
                  <h3
                    id={dialogIds.confirmTitle}
                    className="font-display text-base font-semibold"
                    style={{ color: '#0f1611' }}
                  >
                    {t('seriespill.booking.confirmTitle')}
                  </h3>
                </div>
              </div>
              <div style={{ padding: '16px 18px' }}>
                {isPlacement ? (
                  <>
                    <p className="text-sm text-ink-1">
                      {t('seriespill.booking.confirmClearA')}{' '}
                      <span className="font-semibold">{placedMatchCount}</span>
                      {t(
                        placedMatchCount === 1
                          ? 'seriespill.booking.confirmClearBOne'
                          : 'seriespill.booking.confirmClearBMany',
                      )}
                    </p>
                    <p className="text-[12px] text-ink-3 mt-2">
                      {t('seriespill.booking.confirmClearNote')}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-ink-1">
                      {t('seriespill.booking.confirmRemoveA')}
                      <span className="font-semibold">{serieBookingCount}</span>{' '}
                      {t(
                        serieBookingCount === 1
                          ? 'seriespill.booking.confirmRemoveBOne'
                          : 'seriespill.booking.confirmRemoveBMany',
                      )}
                    </p>
                    {span && (
                      <p className="text-[12px] text-ink-3 mt-2 font-mono">
                        {t('seriespill.booking.period', { span })}
                      </p>
                    )}
                    <p className="text-[12px] text-ink-3 mt-2">
                      {t('seriespill.booking.confirmRemoveNote')}
                    </p>
                  </>
                )}
              </div>
              <div
                style={{
                  padding: '10px 18px',
                  borderTop: '1px solid #e7eae3',
                  background: '#fafbf7',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  onClick={() => setConfirmRemove(null)}
                  className="btn-ghost text-xs"
                >
                  {t('seriespill.booking.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmRemove(null);
                    if (isPlacement) clearPlacement();
                    else clearReservations();
                  }}
                  className="text-[12px] px-3 py-1.5 rounded-lg font-semibold"
                  style={{ background: 'var(--rose)', color: '#fff' }}
                >
                  {isPlacement
                    ? t('seriespill.booking.clearPlacement')
                    : t('seriespill.booking.clearBookings')}
                </button>
              </div>
            </Dialog>
          );
        })()}

      {slotNeedPanel()}

      <div className="card-elev p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {t('seriespill.booking.calendarBookings')}
          </h4>
          <button
            type="button"
            onClick={loadBookingList}
            className="text-[11px] font-mono text-lime-deep underline"
          >
            {bookingList === null
              ? t('seriespill.booking.showBookings')
              : t('seriespill.booking.refresh')}
          </button>
        </div>
        {bookingList === null ? (
          <p className="text-[11px] text-ink-3 italic">
            {t('seriespill.booking.showBookingsHint')}
          </p>
        ) : bookingList.length === 0 ? (
          <p className="text-[11px] text-ink-3 italic">{t('seriespill.booking.noBookings')}</p>
        ) : (
          (() => {
            // Show only bookings that overlap the time-rooms this tournament uses.
            const sm = (t) => {
              const [h, m] = (t || '0:0').split(':').map(Number);
              return h * 60 + (m || 0);
            };
            const _startISO = draft.bookingStart || series?.config?.seasonStart || null;
            const _endISO = reserveEndISO();
            const win = [];
            (draft.slotRooms || []).forEach((room) => {
              const s = sm(room.startTime);
              const e = room.endTime
                ? sm(room.endTime)
                : s + (Number(draft.slotDuration) || 90) * Math.max(1, Number(room.slotCount) || 1);
              let dts = [];
              if (!isSeriespill || room.recurring === false) {
                if (room.singleDate) dts = [room.singleDate];
              } else if (_startISO && _endISO) {
                const freq = room.recurFreq || 'weekly';
                const interval = Math.max(1, Number(room.recurInterval) || 1);
                const man0 = (room.weekdays || []).map((d) => (Number(d) + 6) % 7);
                const re = room.recurEnd || 'until';
                let endType = 'until',
                  count,
                  until = _endISO;
                if (re === 'count') {
                  endType = 'count';
                  count = Math.max(1, Number(room.recurCount) || 1);
                } else if (re === 'until') {
                  until = room.recurUntil || _endISO;
                }
                dts = expandRecurrence(_startISO, {
                  freq,
                  interval,
                  weekdays: freq === 'weekly' ? man0 : [],
                  endType,
                  count,
                  until,
                });
              }
              dts.forEach((d) => win.push({ date: d, s, e }));
            });
            if (win.length === 0)
              return (
                <p className="text-[11px] text-ink-3 italic">
                  {t('seriespill.booking.pickDateForBookings')}
                </p>
              );
            // Show bookings in the time-room PLUS always the tournament's OWN
            // reserved bookings (they should not disappear from the list just
            // because you move the time-room — only when you actually move them).
            const relevant = bookingList.filter(
              (b) =>
                b.seriesId === series.id ||
                win.some((w) => b.date === w.date && sm(b.startTime) < w.e && sm(b.endTime) > w.s),
            );
            const mine = relevant.filter((b) => b.seriesId === series.id);
            if (relevant.length === 0)
              return (
                <p className="text-[11px] text-ink-3 italic">
                  {t('seriespill.booking.noBookingsInWindow')}
                </p>
              );
            const byDate = {};
            relevant.forEach((b) => {
              (byDate[b.date] = byDate[b.date] || []).push(b);
            });
            const dates = Object.keys(byDate).sort();
            return (
              <>
                <p className="text-[11px] text-ink-2 mb-2">
                  {t(
                    relevant.length === 1
                      ? 'seriespill.booking.relevantOne'
                      : 'seriespill.booking.relevantMany',
                    { count: String(relevant.length) },
                  )}{' '}
                  <span className="font-semibold" style={{ color: 'var(--lime-text)' }}>
                    {mine.length}
                  </span>{' '}
                  {t('seriespill.booking.fromThisTournament')}
                </p>
                <div className="max-h-80 overflow-y-auto space-y-1">
                  {dates.map((date) => {
                    const open = !!openBkDates[date];
                    const items = byDate[date].sort(
                      (a, b) =>
                        (a.startTime || '').localeCompare(b.startTime || '') || a.court - b.court,
                    );
                    const dt = new Date(date + 'T12:00:00');
                    const dayName = t(WEEKDAY_SHORT_KEYS[dt.getDay()]);
                    const mnd = t(MONTH_SHORT_KEYS[dt.getMonth()]);
                    const mineHere = items.filter((b) => b.seriesId === series.id).length;
                    return (
                      <div key={date} className="border border-line rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setOpenBkDates((o) => ({ ...o, [date]: !o[date] }))}
                          className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left hover:bg-bg-soft"
                        >
                          <span className="text-[12px] font-medium text-ink-1">
                            {t('seriespill.booking.weekdayDayMonth', {
                              wd: dayName,
                              day: String(dt.getDate()),
                              month: mnd,
                            })}
                          </span>
                          <span className="text-[11px] font-mono text-ink-3">
                            {t(
                              items.length === 1
                                ? 'seriespill.booking.bookingCountOne'
                                : 'seriespill.booking.bookingCountMany',
                              { count: String(items.length) },
                            )}
                            {mineHere
                              ? t('seriespill.booking.ofWhichSeries', {
                                  count: String(mineHere),
                                })
                              : ''}{' '}
                            {open ? '▲' : '▼'}
                          </span>
                        </button>
                        {open && (
                          <div className="px-2.5 pb-1.5 space-y-0.5">
                            {items.map((b) => (
                              <div
                                key={b.id}
                                className="flex items-center justify-between gap-2 text-[11px] py-0.5 px-2 rounded"
                                style={{
                                  background:
                                    b.seriesId === series.id
                                      ? 'var(--lime-bg)'
                                      : 'rgba(0,0,0,0.02)',
                                }}
                              >
                                <span className="font-mono text-ink-2">
                                  {t('seriespill.booking.timeAndCourt', {
                                    start: b.startTime,
                                    end: b.endTime,
                                    court: String(b.court),
                                  })}
                                </span>
                                <span className="text-ink-3 truncate ml-2">
                                  {b.title ||
                                    (b.source === 'tournament'
                                      ? t('seriespill.booking.seriesLabel')
                                      : b.bookedBy?.name || '—')}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()
        )}
      </div>
      {reservePreview && (
        <Dialog
          labelledBy={dialogIds.reserveTitle}
          describedBy={dialogIds.reserveLead}
          onClose={() => setReservePreview(null)}
          showClose={false}
          backdropClassName="fixed inset-0 z-[60] flex items-center justify-center p-3 bg-ink-1/50 backdrop-blur-sm"
          className="bg-paper rounded-xl shadow-xl max-w-lg w-full"
          style={{ maxHeight: 'calc(100dvh - 24px)', display: 'flex', flexDirection: 'column' }}
        >
          <div className="p-4 border-b border-line">
            <h3
              id={dialogIds.reserveTitle}
              className="font-display text-lg font-semibold text-ink-1"
            >
              {t('seriespill.booking.reserveModalTitle')}
            </h3>
            <p id={dialogIds.reserveLead} className="text-[12px] text-ink-3 mt-0.5">
              {reservePreview.startISO} → {reservePreview.endISO}
            </p>
          </div>
          <div className="p-4 overflow-y-auto space-y-2">
            {(() => {
              const n = reservePreview.toCreate.length;
              const resName =
                series?.config?.name || draft?.name || t('seriespill.booking.reservationFallback');
              const byDate = {};
              reservePreview.toCreate.forEach((b) => {
                (byDate[b.date] = byDate[b.date] || []).push(b);
              });
              const dates = Object.keys(byDate).sort();
              return (
                <>
                  <p className="text-sm text-ink-2">
                    <span className="font-semibold text-ink-1">{n}</span>
                    {t(
                      n === 1
                        ? 'seriespill.booking.willReserveOne'
                        : 'seriespill.booking.willReserveMany',
                    )}
                    {onPlaceMatches ? t('seriespill.booking.andPlaceMatches') : ''}.
                  </p>
                  <div
                    className="rounded-lg border p-2.5 text-[12px]"
                    style={{ background: 'var(--lime-bg)', borderColor: 'var(--lime-deep)' }}
                  >
                    <div className="text-ink-3">{t('seriespill.booking.reservationName')}</div>
                    <div className="font-semibold text-ink-1 text-sm">{resName}</div>
                    <div className="text-[10px] text-ink-3 mt-0.5 font-mono">
                      {t('seriespill.booking.tournamentId', {
                        id: series?.id || '—',
                      })}
                    </div>
                  </div>
                  <div className="rounded-lg border border-line p-2.5">
                    <div className="text-[11px] font-semibold text-ink-2 mb-1">
                      {t('seriespill.booking.thisIsReserved')}
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-0.5 text-[12px] text-ink-2">
                      {dates.slice(0, 30).map((d) => {
                        const items = byDate[d];
                        // deviation: type-only (monolith is @ts-nocheck) — Set members infer `unknown`; cast for arithmetic.
                        const courts = [...new Set(items.map((b) => b.court))].sort(
                          (a, b) => (a as number) - (b as number),
                        );
                        const time = `${items[0].startTime}–${items[0].endTime}`;
                        return (
                          <div key={d} className="font-mono">
                            {t('seriespill.booking.dateTimeCourts', {
                              date: d,
                              time,
                              courts: courts.join(', '),
                            })}
                          </div>
                        );
                      })}
                      {dates.length > 30 && (
                        <div className="text-[11px] text-ink-3 italic">
                          {t('seriespill.booking.andMoreDays', {
                            count: String(dates.length - 30),
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
            {reservePreview.conflicts.length > 0 ? (
              <div
                className="rounded-lg border border-line p-3"
                style={{ background: 'rgba(220,38,38,0.05)' }}
              >
                <p className="text-[12px] font-semibold" style={{ color: '#b45309' }}>
                  {t('seriespill.booking.conflictsHeading', {
                    count: String(reservePreview.conflicts.length),
                  })}
                </p>
                <div className="mt-1.5 max-h-40 overflow-y-auto space-y-0.5">
                  {reservePreview.conflicts.slice(0, 40).map((c, i) => (
                    <div key={i} className="text-[11px] text-ink-3 font-mono">
                      {t('seriespill.booking.conflictLine', {
                        date: c.date,
                        court: String(c.court),
                        start: c.startTime,
                        end: c.endTime,
                      })}
                    </div>
                  ))}
                  {reservePreview.conflicts.length > 40 && (
                    <div className="text-[11px] text-ink-3 italic">
                      {t('seriespill.booking.andMore', {
                        count: String(reservePreview.conflicts.length - 40),
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-[12px] text-ink-3">{t('seriespill.booking.noConflicts')}</p>
            )}
          </div>
          <div className="p-4 border-t border-line flex items-center justify-end gap-2">
            <button onClick={() => setReservePreview(null)} className="btn-outline text-sm">
              {t('seriespill.booking.cancel')}
            </button>
            <button
              onClick={() => commitAndPlace(false)}
              disabled={reserveBusy || reservePreview.toCreate.length === 0}
              className="btn-lime text-sm disabled:opacity-50"
            >
              {reserveBusy
                ? t('seriespill.booking.working')
                : t('seriespill.booking.reserveCourtsCount', {
                    count: String(reservePreview.toCreate.length),
                  })}
            </button>
          </div>
        </Dialog>
      )}

      {/* STATUS MODAL — shown after «Reserver og plasser» (and standalone «Plasser»).
          Shows a summary and lets the admin free up surplus (empty) bookings. */}
      {statusReport && (
        <Dialog
          labelledBy={dialogIds.statusTitle}
          onClose={() => setStatusReport(null)}
          showClose={false}
          backdropClassName="fixed inset-0 z-[60] flex items-center justify-center p-3 bg-ink-1/50 backdrop-blur-sm"
          className="bg-paper rounded-xl shadow-xl max-w-lg w-full"
          style={{ maxHeight: 'calc(100dvh - 24px)', display: 'flex', flexDirection: 'column' }}
        >
          <div className="p-4 border-b border-line">
            <h3
              id={dialogIds.statusTitle}
              className="font-display text-lg font-semibold text-ink-1"
            >
              {statusReport.placeOnly
                ? t('seriespill.booking.statusPlaced')
                : statusReport.placeMode
                  ? t('seriespill.booking.statusReservedAndPlaced')
                  : t('seriespill.booking.statusReserved')}
            </h3>
            {statusReport.name && !statusReport.placeOnly && (
              <p className="text-[12px] text-ink-3 mt-0.5">«{statusReport.name}»</p>
            )}
          </div>
          <div className="p-4 overflow-y-auto space-y-2">
            {!statusReport.placeOnly && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink-2">{t('seriespill.booking.statSlotsReserved')}</span>
                <span className="font-semibold text-ink-1">{statusReport.reserved}</span>
              </div>
            )}
            {statusReport.placeOnly && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink-2">{t('seriespill.booking.statSlotsAvailable')}</span>
                <span className="font-semibold text-ink-1">{statusReport.reserved}</span>
              </div>
            )}
            {statusReport.conflicts > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink-2">{t('seriespill.booking.statSkipped')}</span>
                <span className="font-semibold" style={{ color: '#b45309' }}>
                  {statusReport.conflicts}
                </span>
              </div>
            )}
            {(statusReport.placeMode || statusReport.placeOnly) && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-2">{t('seriespill.booking.statPlaced')}</span>
                  <span className="font-semibold" style={{ color: 'var(--lime-text)' }}>
                    {statusReport.placed}
                  </span>
                </div>
                {statusReport.unplaced > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ink-2">{t('seriespill.booking.statUnplaced')}</span>
                    <span className="font-semibold" style={{ color: '#b45309' }}>
                      {statusReport.unplaced}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-2">{t('seriespill.booking.statEmpty')}</span>
                  <span className="font-semibold text-ink-1">{statusReport.empty}</span>
                </div>
              </>
            )}
            {statusReport.slots &&
              statusReport.slots.length > 0 &&
              (() => {
                const byDate = {};
                statusReport.slots.forEach((s) => {
                  (byDate[s.date] = byDate[s.date] || []).push(s);
                });
                const dts = Object.keys(byDate).sort();
                return (
                  <div className="rounded-lg border border-line p-2.5 mt-1">
                    <div className="text-[11px] font-semibold text-ink-2 mb-1">
                      {t('seriespill.booking.reservedInCalendar')}
                    </div>
                    <div className="max-h-44 overflow-y-auto space-y-0.5 text-[12px] text-ink-2">
                      {dts.slice(0, 30).map((d) => {
                        const items = byDate[d];
                        // deviation: type-only (monolith is @ts-nocheck) — Set members infer `unknown`; cast for arithmetic.
                        const courts = [...new Set(items.map((s) => s.court))].sort(
                          (a, b) => (a as number) - (b as number),
                        );
                        return (
                          <div key={d} className="font-mono">
                            {t('seriespill.booking.dateTimeCourts', {
                              date: d,
                              time: `${items[0].startTime}–${items[0].endTime}`,
                              courts: courts.join(', '),
                            })}
                          </div>
                        );
                      })}
                      {dts.length > 30 && (
                        <div className="text-[11px] text-ink-3 italic">
                          {t('seriespill.booking.andMoreDays', {
                            count: String(dts.length - 30),
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            {statusReport.removed != null && (
              <p className="text-[12px]" style={{ color: 'var(--lime-text)' }}>
                {t(
                  statusReport.removed === 1
                    ? 'seriespill.booking.freedOne'
                    : 'seriespill.booking.freedMany',
                  { count: String(statusReport.removed) },
                )}
              </p>
            )}
            {statusReport.error && (
              <p className="text-[12px]" style={{ color: '#dc2626' }}>
                {statusReport.error}
              </p>
            )}
            {statusReport.empty > 0 &&
              statusReport.emptyIds &&
              statusReport.emptyIds.length > 0 && (
                <p className="text-[12px] text-ink-3 pt-1">
                  {t('seriespill.booking.surplusQuestion')}
                </p>
              )}
          </div>
          <div className="p-4 border-t border-line flex items-center justify-end gap-2">
            {statusReport.emptyIds && statusReport.emptyIds.length > 0 && (
              <button
                onClick={removeSurplus}
                disabled={reserveBusy}
                className="text-sm px-3 py-2 rounded-lg border border-rose text-rose hover:bg-rose-soft/30 disabled:opacity-50"
              >
                {reserveBusy
                  ? t('seriespill.booking.removing')
                  : t('seriespill.booking.removeSurplus', {
                      count: String(statusReport.empty),
                    })}
              </button>
            )}
            <button onClick={() => setStatusReport(null)} className="btn-lime text-sm">
              {t('seriespill.booking.done')}
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
